import { useEffect, useMemo, useRef, useState } from "react";
import { useStable } from "../context/StableContext";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { errText } from "../lib/api";
import { matchHorse } from "../lib/horseSearch";
import { sortByName } from "../lib/people";
import { InfoHint } from "../components/InfoHint";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { DateField } from "../components/DateField";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ViewTabs } from "../components/ViewTabs";
import { SmartSearchPicker } from "../components/SmartSearchPicker";
import { PersonSearchCreate } from "../components/PersonSearchCreate";
import { MobileSheet } from "../components/MobileSheet";
import { EquiroCalendar } from "../components/EquiroCalendar";
import { CalendarDays } from "lucide-react";
import { useIsMobile } from "../hooks/useIsMobile";
import { useMobileDialogStyle } from "../hooks/useVisualViewport";
import { Plus, ChevronLeft, ChevronRight, Settings2, Trash2, Copy, Loader2, X, Repeat, Search, RotateCcw, Eye, EyeOff, Sparkles, Star, GraduationCap, ClipboardList, ClipboardCheck, Check, MoreVertical, StickyNote, Pencil, ArrowRightLeft, UserMinus, AlertTriangle } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";

const PROVA_INK = "#6D28D9"; // high-contrast accent for PROVA (rendered on a white chip → legible on all 16 pastels)
function instrColor(id) { if (!id) return "#9AA39C"; let h = 0; for (const ch of String(id)) h = (h * 31 + ch.charCodeAt(0)) >>> 0; return COLORS[h % COLORS.length]; }
const CARD_MIN = 132;

const WD = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const WD_FULL = ["lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato", "domenica"];
const COLORS = [
  "#6E8C7B", "#8FB09A", "#B7D1BE", "#CBDBB0", "#E7DCA6", "#F2D08A", "#F5BE7A", "#EFA463",
  "#E68A5C", "#D3765A", "#C86E6E", "#C99AA6", "#9BA6D4", "#8FB2CE", "#8C7F9E", "#B39371",
];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const wdOf = (d) => (d.getDay() + 6) % 7;
const startOfWeek = (d) => addDays(d, -wdOf(d));
const fmtD = (d) => d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
const fmtLong = (dIso) => new Date(dIso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
const norm = (s) => (s || "").trim().toLowerCase();
const fmtDayFull = (dIso) => new Date(dIso + "T00:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
function groupRecordsByDate(records) {
  const m = new Map();
  for (const r of (records || [])) { if (!m.has(r.date)) m.set(r.date, []); m.get(r.date).push(r); }
  return [...m.entries()].map(([date, items]) => ({ date, items })).sort((a, b) => a.date.localeCompare(b.date));
}
const minutesOf = (t) => { const [h, m] = String(t).split(":").map(Number); return (h || 0) * 60 + (m || 0); };
const hmOf = (mins) => { const x = ((mins % 1440) + 1440) % 1440; return `${String(Math.floor(x / 60)).padStart(2, "0")}:${String(x % 60).padStart(2, "0")}`; };

function textOn(hex) {
  const h = (hex || "#1B3B2B").replace("#", "");
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62 ? "#1B3B2B" : "#FFFFFF";
}
function recurrencePreview(weekdays, interval) {
  if (!weekdays || !weekdays.length) return "Seleziona i giorni della settimana.";
  const days = [...weekdays].sort((a, b) => a - b).map((n) => WD_FULL[n]).join(", ");
  return interval <= 1 ? `Si ripete ogni ${days}.` : `Si ripete ${days} ogni ${interval} settimane.`;
}
const FREQ_OPTS = [["1", "Ogni settimana"], ["2", "Ogni 2 settimane"], ["3", "Ogni 3 settimane"], ["4", "Ogni 4 settimane"], ["custom", "Personalizzata…"]];
const VIEWS = [["day", "Oggi"], ["3", "3 giorni"], ["week", "Settimana"], ["month", "Mese"]];
const defaultView = () => (typeof window !== "undefined" && window.matchMedia("(min-width:1024px)").matches ? "week" : "day");

const EMPTY_LESSON = {
  mode: "single", date: iso(new Date()), start_time: "17:00", end_time: "18:00",
  title: "", color: COLORS[0], activity_type: "", instructor: "", notes: "",
  participants: [], lesson_horses: [], recurrence: { freq: "weekly", weekdays: [], interval: 1, start_date: iso(new Date()), end_date: "" },
};

function buildBands(slots, occ, cols) {
  const wds = new Set(cols.map(wdOf));
  const colSet = new Set(cols.map(iso));
  const map = new Map();
  (slots || []).forEach((sl) => { if ((sl.weekdays || []).some((w) => wds.has(w)) && !map.has(sl.start)) map.set(sl.start, sl.end); });
  (occ || []).forEach((o) => { if (colSet.has(o.date) && !map.has(o.start_time)) map.set(o.start_time, o.end_time); });
  return [...map.entries()].map(([start, end]) => ({ start, end })).sort((a, b) => a.start.localeCompare(b.start));
}

export default function Calendar() {
  const dlgStyle = useMobileDialogStyle();
  const { current, perms, role } = useStable();
  const [view, setView] = useState(defaultView());
  const [selDay, setSelDay] = useState(iso(new Date()));
  const [operativeOnly, setOperativeOnly] = useState(false);
  const [occ, setOcc] = useState(null);
  const [horses, setHorses] = useState([]);
  const [settings, setSettings] = useState({ slots: [], holidays_enabled: false, closing_days: [], weekend_days: [5, 6] });
  const [specials, setSpecials] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [orig, setOrig] = useState(null);
  const [makeRec, setMakeRec] = useState(false);
  const [scopeAsk, setScopeAsk] = useState(null);
  const [saving, setSaving] = useState(false);
  const [setOpen, setSetOpen] = useState(false);
  const [avail, setAvail] = useState(null);
  const [showCancelled, setShowCancelled] = useState(true);
  const [findOpen, setFindOpen] = useState(false);
  const { user, updatePreferences } = useAuth();
  const [instructors, setInstructors] = useState([]);
  const [instrFilter, setInstrFilter] = useState("all");
  const viewInit = useRef(false);
  const mineInit = useRef(false);
  const myInstructorId = useMemo(() => instructors.find((i) => i.user_id && i.user_id === user?.id)?.id || null, [instructors, user]);
  const instrColorMap = useMemo(() => { const m = {}; instructors.forEach((i) => { m[i.id] = i.color || instrColor(i.id); }); return m; }, [instructors]);
  const [arenas, setArenas] = useState([]);
  const [attOcc, setAttOcc] = useState(null);
  const [sp, setSp] = useSearchParams();
  const [regOpen, setRegOpen] = useState(false);
  const [absOpen, setAbsOpen] = useState(false);
  const [goOpen, setGoOpen] = useState(false);
  const [conflictAsk, setConflictAsk] = useState(null);
  const isMobile = useIsMobile();

  const canView = ["staff", "manager", "admin"].includes(role);
  const canManage = ["manager", "admin"].includes(role);
  const refreshInstructors = () => api.get(`/stables/${current.id}/instructors`).then((r) => setInstructors(r.data || [])).catch(() => {});
  const closing = useMemo(() => new Set(settings.closing_days || []), [settings]);
  const weekend = useMemo(() => new Set(settings.weekend_days ?? [5, 6]), [settings]);

  const gridDays = useMemo(() => {
    const s = new Date(selDay + "T00:00:00");
    if (view === "day") return [s];
    if (view === "3") return [0, 1, 2].map((i) => addDays(s, i));
    if (view === "week") { const w = startOfWeek(s); return [...Array(7)].map((_, i) => addDays(w, i)); }
    const first = new Date(s.getFullYear(), s.getMonth(), 1);
    const gridStart = startOfWeek(first);
    const dim = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    const cells = Math.ceil((wdOf(first) + dim) / 7) * 7;
    return [...Array(cells)].map((_, i) => addDays(gridStart, i));
  }, [view, selDay]);
  const rangeStart = iso(gridDays[0]);
  const rangeEnd = iso(gridDays[gridDays.length - 1]);
  const specialMap = useMemo(() => { const m = {}; specials.forEach((s) => { m[s.date] = s; }); return m; }, [specials]);
  const filteredOcc = useMemo(() => {
    if (!occ || instrFilter === "all") return occ;
    const target = instrFilter === "mine" ? myInstructorId : instrFilter;
    return occ.filter((o) => o.instructor_id === target);
  }, [occ, instrFilter, myInstructorId]);

  const load = () => {
    if (!current || !canView) return;
    api.get(`/stables/${current.id}/calendar?start=${rangeStart}&end=${rangeEnd}&include_cancelled=${showCancelled}`)
      .then((r) => setOcc(r.data)).catch((e) => toast.error(errText(e.response?.data?.detail)));
    api.get(`/stables/${current.id}/calendar/special-days?start=${rangeStart}&end=${rangeEnd}`)
      .then((r) => setSpecials(r.data)).catch(() => setSpecials([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [current, rangeStart, rangeEnd, canView, showCancelled]);
  useEffect(() => {
    if (!current || !canView) return;
    api.get(`/stables/${current.id}/horses`).then((r) => setHorses(r.data)).catch(() => {});
    api.get(`/stables/${current.id}/calendar/settings`).then((r) => setSettings(r.data || { slots: [] })).catch(() => {});
    api.get(`/stables/${current.id}/instructors`).then((r) => setInstructors(r.data || [])).catch(() => {});
    api.get(`/stables/${current.id}/arenas`).then((r) => setArenas(r.data || [])).catch(() => {});
  }, [current, canView]);

  // Deep-link: ?att=<occ_id>[&attdate=<iso>] opens Presenze for that occurrence. When a date is
  // given (e.g. from Attività Cavalli), first jump to that day so the occurrence is in range.
  useEffect(() => {
    const attId = sp.get("att");
    if (!attId) return;
    const attDate = sp.get("attdate");
    if (attDate && selDay !== attDate) { setSelDay(attDate); setView("day"); return; }
    if (!occ) return;
    const target = occ.find((o) => o.id === attId);
    if (!target) return;
    setAttOcc(target);
    const p = new URLSearchParams(sp); p.delete("att"); p.delete("attdate"); setSp(p, { replace: true });
    // eslint-disable-next-line
  }, [occ, sp, selDay]);
  useEffect(() => {
    if (viewInit.current || !user) return;
    viewInit.current = true;
    const p = user.preferences || {};
    const isDesktop = window.matchMedia("(min-width:1024px)").matches;
    const v = isDesktop ? p.calendar_view_desktop : p.calendar_view_mobile;
    if (v && VIEWS.some(([x]) => x === v)) setView(v);
  }, [user]);

  useEffect(() => {
    if (mineInit.current || !myInstructorId) return;
    mineInit.current = true;
    if (role === "staff") setInstrFilter("mine");
  }, [myInstructorId, role]);

  const saveDefaultView = async () => {
    const isDesktop = window.matchMedia("(min-width:1024px)").matches;
    await updatePreferences({ [isDesktop ? "calendar_view_desktop" : "calendar_view_mobile"]: view });
    toast.success(`Vista predefinita (${isDesktop ? "desktop" : "mobile"}) impostata su "${VIEWS.find(([x]) => x === view)?.[1]}"`);
  };
  const setHabitual = async (i, horseId) => {
    const p = dialog.participants.find((x) => x._k === i);
    if (!p?._student_id) return;
    try {
      await api.put(`/stables/${current.id}/students/${p._student_id}`, { habitual_horse_id: horseId });
      setP(i, { _habitual_horse_id: horseId, _habitual_horse_name: horseId ? (horses.find((h) => h.id === horseId)?.display_name || "") : null });
      toast.success(horseId ? "Cavallo abituale impostato" : "Cavallo abituale rimosso");
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };

  const refDate = dialog ? (dialog.mode === "single" ? dialog.date : dialog.recurrence.start_date) : null;
  useEffect(() => {
    if (!dialog || !current) { setAvail(null); return; }
    let active = true;
    const t = setTimeout(() => {
      const ex = orig ? `&exclude=${orig.series_id || orig.id}` : "";
      api.get(`/stables/${current.id}/calendar/availability?date=${refDate}&start=${dialog.start_time}&end=${dialog.end_time}${ex}`)
        .then((r) => { if (active) setAvail(r.data); }).catch(() => { if (active) setAvail(null); });
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [dialog?.start_time, dialog?.end_time, refDate, current, orig]); // eslint-disable-line

  if (!canView)
    return <div className="max-w-md mx-auto mt-16 text-center" data-testid="calendar-no-access">
      <p className="font-display text-xl font-bold text-[#1B3B2B]">Calendario non disponibile</p>
      <p className="text-sm text-[#5F6B64] mt-2">Il calendario delle lezioni è riservato allo staff della scuderia.</p>
    </div>;

  const byDay = (dIso) => (filteredOcc || []).filter((o) => o.date === dIso);
  const byDayTime = (dIso, start) => (filteredOcc || []).filter((o) => o.date === dIso && o.start_time === start);

  const openNew = (dIso, slot, prefill) => {
    const d = dIso || selDay;
    setOrig(null); setMakeRec(false);
    if (closing.has(wdOf(new Date(d + "T00:00:00")))) toast.warning("Giorno di chiusura abituale — puoi comunque creare la lezione.");
    setDialog({ ...JSON.parse(JSON.stringify(EMPTY_LESSON)), date: d,
      start_time: slot?.start || "17:00", end_time: slot?.end || "18:00", color: slot?.color || COLORS[0],
      recurrence: { ...EMPTY_LESSON.recurrence, start_date: d, weekdays: [wdOf(new Date(d + "T00:00:00"))] },
      ...(prefill || {}) });
  };
  const openAttendance = (o) => { setAttOcc(o); };
  const openEdit = (o) => {
    setOrig(o); setMakeRec(false);
    const rec = o.recurrence || {};
    setDialog({ mode: o.series_id ? "series" : "single", date: o.date, start_time: o.start_time, end_time: o.end_time,
      title: o.title || "", color: o.color || COLORS[0], activity_type: o.activity_type || "", instructor: o.instructor || "",
      notes: o.notes || "", instructor_id: o.instructor_id || "",
      participants: (o.participants || []).map((p) => ({ ...p, _k: crypto.randomUUID?.() || Math.random().toString(36), _student_id: p.student_id || null })),
      lesson_horses: o.lesson_horses || [],
      recurrence: { freq: "weekly", weekdays: rec.weekdays || [], interval: rec.interval || 1,
        start_date: rec.start_date || o.date, end_date: rec.end_date || "" }, series_id: o.series_id });
  };

  const setP = (k, patch) => setDialog((d) => ({ ...d, participants: d.participants.map((p) => p._k === k ? { ...p, ...patch } : p) }));
  const addP = () => setDialog((d) => ({ ...d, participants: [...d.participants, { _k: crypto.randomUUID?.() || Math.random().toString(36), name: "", horse_id: "", horse_name: "", kind: "normale", notes: "" }] }));
  const delP = (k) => setDialog((d) => ({ ...d, participants: d.participants.filter((p) => p._k !== k) }));
  const toggleWd = (n) => setDialog((d) => { const w = d.recurrence.weekdays.includes(n) ? d.recurrence.weekdays.filter((x) => x !== n) : [...d.recurrence.weekdays, n]; return { ...d, recurrence: { ...d.recurrence, weekdays: w } }; });
  // Changing the start time PRESERVES the current lesson duration (default 60 on new lessons).
  const changeStart = (v) => setDialog((d) => { const dur = minutesOf(d.end_time) - minutesOf(d.start_time); const nd = dur > 0 ? dur : 60; return { ...d, start_time: v, end_time: hmOf(minutesOf(v) + nd) }; });

  const payload = () => ({
    start_time: dialog.start_time, end_time: dialog.end_time, title: dialog.title || null, color: dialog.color,
    activity_type: dialog.activity_type || null, instructor: dialog.instructor || null, instructor_id: dialog.instructor_id || null, notes: dialog.notes || null,
    participants: dialog.participants.filter((p) => p.name.trim() || p.horse_id || (p.horse_name || "").trim())
      .map((p) => ({ name: p.name, horse_id: p.horse_id || null, horse_name: p.horse_id ? null : (p.horse_name || null), kind: p.kind, notes: p.notes || null })),
    lesson_horses: (dialog.lesson_horses || []).filter(Boolean),
  });
  const recPayload = () => ({ ...dialog.recurrence, end_date: dialog.recurrence.end_date || null });
  const validParts = () => { const seen = new Set(); for (const p of dialog.participants) { if (p.horse_id) { if (seen.has(p.horse_id)) return "Lo stesso cavallo è assegnato a più partecipanti."; seen.add(p.horse_id); } } return null; };

  const doSave = async (scope) => {
    setSaving(true);
    try {
      let res;
      if (orig && orig.series_id) res = await api.put(`/stables/${current.id}/calendar/lessons/${orig.series_id}`, { ...payload(), mode: "series", scope, occurrence_date: orig.date, date: dialog.date, recurrence: recPayload() });
      else if (orig) res = await api.put(`/stables/${current.id}/calendar/lessons/${orig.id}`, { ...payload(), mode: "single", date: dialog.date });
      else if (dialog.mode === "series") { if (!dialog.recurrence.weekdays.length) { toast.error("Seleziona almeno un giorno"); setSaving(false); return; } res = await api.post(`/stables/${current.id}/calendar/lessons`, { ...payload(), mode: "series", recurrence: recPayload() }); }
      else res = await api.post(`/stables/${current.id}/calendar/lessons`, { ...payload(), mode: "single", date: dialog.date });
      const conflicts = res.data?.conflicts || [];
      if (conflicts.length) toast.warning(`Conflitto cavallo: ${conflicts.map((c) => `${c.horse_name} (${c.lesson} ${c.time})`).join(", ")}`);
      else toast.success("Lezione salvata");
      setDialog(null); setOrig(null); setScopeAsk(null); load();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const doMakeRecurring = async () => {
    if (!dialog.recurrence.weekdays.length) { toast.error("Seleziona almeno un giorno"); return; }
    setSaving(true);
    try {
      await api.put(`/stables/${current.id}/calendar/lessons/${orig.id}`, { ...payload(), mode: "single", date: dialog.date });
      await api.post(`/stables/${current.id}/calendar/lessons/${orig.id}/make-recurring`, { recurrence: recPayload() });
      toast.success("Lezione resa ricorrente"); setDialog(null); setOrig(null); setMakeRec(false); load();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  // Smart Conflict merge: add ALL valid new-form participants into the EXISTING lesson/occurrence
  // (occurrence-safe via add-participant: recurring → only that occurrence, never series/template).
  // No second lesson is created (no residue).
  const doMerge = async (target) => {
    const valid = dialog.participants.filter((p) => (p.name || "").trim() || p.horse_id || (p.horse_name || "").trim());
    if (!valid.length) { toast.error("Nessun partecipante da aggiungere alla lezione esistente"); return; }
    setSaving(true);
    try {
      let added = 0;
      for (const p of valid) {
        await api.post(`/stables/${current.id}/calendar/occurrence/add-participant`, {
          occ_key: target.occ_key,
          participant: { name: (p.name || "").trim(), horse_id: p.horse_id || null,
            horse_name: p.horse_id ? null : (p.horse_name || null), kind: p.kind || "normale", notes: p.notes || null },
        });
        added += 1;
      }
      toast.success(added === 1 ? "Partecipante aggiunto alla lezione esistente" : `${added} partecipanti aggiunti alla lezione esistente`);
      setConflictAsk(null); setDialog(null); setOrig(null); load();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const doCreateAnyway = () => { setConflictAsk(null); doSave(); };
  const onSave = async () => {
    if (!dialog.start_time || !dialog.end_time || dialog.end_time <= dialog.start_time) { toast.error("Orario non valido"); return; }
    const err = validParts(); if (err) { toast.error(err); return; }
    if (makeRec) return doMakeRecurring();
    if (orig && orig.series_id) return setScopeAsk("edit");
    if (orig) return doSave();
    // Smart Conflict: ONLY on NEW single-lesson creation with an instructor selected.
    if (dialog.mode === "single" && dialog.instructor_id) {
      try {
        const r = await api.post(`/stables/${current.id}/calendar/conflict-check`,
          { date: dialog.date, start_time: dialog.start_time, end_time: dialog.end_time, instructor_id: dialog.instructor_id });
        if (r.data?.result && r.data.result !== "no_conflict") { setConflictAsk(r.data); return; }
      } catch { /* detection is best-effort: fall through to normal create */ }
    }
    doSave();
  };
  const doDelete = async (scope) => {
    setSaving(true);
    try { const q = orig.series_id ? `?scope=${scope}&date=${orig.date}` : ""; await api.delete(`/stables/${current.id}/calendar/lessons/${orig.series_id || orig.id}${q}`); toast.success("Lezione eliminata"); setDialog(null); setOrig(null); setScopeAsk(null); load(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const onDelete = () => { if (orig.series_id) setScopeAsk("delete"); else doDelete(); };
  const duplicate = () => { const d = { ...dialog, mode: "single" }; delete d.series_id; setOrig(null); setMakeRec(false); setDialog(d); toast.info("Duplica: modifica e salva come nuova lezione"); };
  const restoreOcc = async (o) => { try { await api.post(`/stables/${current.id}/calendar/lessons/${o.series_id}/restore?date=${o.date}`); toast.success("Lezione ripristinata"); load(); } catch (e) { toast.error(errText(e.response?.data?.detail)); } };
  const cancelDay = async (dIso) => { try { const r = await api.post(`/stables/${current.id}/calendar/cancel-day`, { date: dIso }); toast.success(`${r.data.cancelled} lezioni annullate`); load(); } catch (e) { toast.error(errText(e.response?.data?.detail)); } };
  const restoreDay = async (dIso) => { try { const r = await api.post(`/stables/${current.id}/calendar/restore-day`, { date: dIso }); toast.success(`${r.data.restored} lezioni ripristinate`); load(); } catch (e) { toast.error(errText(e.response?.data?.detail)); } };

  const shift = (dir) => {
    const s = new Date(selDay + "T00:00:00");
    if (view === "day") setSelDay(iso(addDays(s, dir)));
    else if (view === "3") setSelDay(iso(addDays(s, dir * 3)));
    else if (view === "week") setSelDay(iso(addDays(s, dir * 7)));
    else setSelDay(iso(new Date(s.getFullYear(), s.getMonth() + dir, 1)));
  };
  const rangeLabel = () => {
    if (view === "month") return new Date(selDay + "T00:00:00").toLocaleDateString("it-IT", { month: "long", year: "numeric" });
    if (view === "day") return fmtLong(selDay);
    return `${fmtD(gridDays[0])} – ${fmtD(gridDays[gridDays.length - 1])} ${gridDays[gridDays.length - 1].getFullYear()}`;
  };

  const isSeries = dialog && dialog.mode === "series";
  const showRec = isSeries || makeRec;
  const cols = (view === "week" && operativeOnly) ? gridDays.filter((d) => !closing.has(wdOf(d))) : gridDays;
  const bands = buildBands(settings.slots, filteredOcc, cols);
  const dayConc = cols.map((d) => {
    const dIso = iso(d); let mx = 1;
    bands.forEach((b) => { const n = (filteredOcc || []).filter((o) => o.date === dIso && o.start_time === b.start).length; if (n > mx) mx = n; });
    return mx;
  });
  const colTemplate = `48px ` + cols.map((d, i) => {
    if (view === "day") return "minmax(0,1fr)";
    const c = dayConc[i] || 1;
    return c > 1 ? `minmax(${c * CARD_MIN}px, ${c}fr)` : `minmax(128px, 1fr)`;
  }).join(" ");

  return (
    <div className="space-y-4 eq-fade-up" data-testid="calendar-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="font-mono-label text-[#8C532B] text-xs">Lezioni e programmazione</p>
          <h1 className="font-display text-2xl lg:text-3xl font-bold text-[#1B3B2B]">Calendario</h1>
        </div>
        <div className="flex flex-col gap-2 w-full sm:w-auto sm:items-end">
          <div className="grid grid-cols-2 gap-2 w-full sm:flex sm:w-auto sm:items-center" data-testid="calendar-primary-actions">
            {perms.canWrite && <Button variant="outline" onClick={() => setFindOpen(true)} data-testid="find-slots-btn" className="w-full justify-center whitespace-nowrap text-xs sm:w-auto sm:text-sm"><Search className="h-4 w-4 mr-2" /><span className="sm:hidden">Disponibilità</span><span className="hidden sm:inline">Trova disponibilità</span></Button>}
            {perms.canWrite && <Button onClick={() => openNew()} data-testid="new-lesson-btn" className="w-full justify-center whitespace-nowrap text-xs sm:w-auto sm:text-sm bg-[#1B3B2B] hover:bg-[#142E1F]"><Plus className="h-4 w-4 mr-2" />Nuova lezione</Button>}
          </div>
          <div className="grid grid-cols-3 gap-2 w-full sm:flex sm:w-auto sm:items-center" data-testid="calendar-tools-row">
            {canView && <Button variant="outline" onClick={() => setRegOpen(true)} data-testid="registro-presenze-btn" className="w-full justify-center whitespace-nowrap text-xs sm:w-auto sm:text-sm px-2 sm:px-3"><ClipboardList className="h-4 w-4 mr-1 sm:mr-2 shrink-0" /><span className="sm:hidden">Registro</span><span className="hidden sm:inline">Registro attività</span></Button>}
            {perms.canManage && <Button variant="outline" onClick={() => setSetOpen(true)} data-testid="calendar-settings-btn" className="w-full justify-center whitespace-nowrap text-xs sm:w-auto sm:text-sm px-2 sm:px-3"><Settings2 className="h-4 w-4 mr-1 sm:mr-2 shrink-0" /><span className="sm:hidden">Slot</span><span className="hidden sm:inline">Slot &amp; festività</span></Button>}
            {perms.canWrite && <Button variant="outline" onClick={() => setAbsOpen(true)} data-testid="absences-btn" className="w-full justify-center whitespace-nowrap text-xs sm:w-auto sm:text-sm px-2 sm:px-3"><UserMinus className="h-4 w-4 mr-1 sm:mr-2 shrink-0" />Assenze</Button>}
          </div>
        </div>
      </div>

      {/* View selector + Vai a — same row on mobile (no wrap, no horizontal scroll) */}
      <div className="flex items-center gap-1.5 sm:gap-2">
        <div className="flex items-center gap-0.5 sm:gap-1 rounded-lg border border-[#E5DEC9] bg-white p-1 w-fit" data-testid="view-selector">
          {VIEWS.map(([v, l]) => (
            <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)}
              className={`px-2 py-1.5 sm:px-3 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${view === v ? "bg-[#1B3B2B] text-white" : "text-[#1B3B2B] hover:bg-[#F0EAE1]"}`}>{l}</button>
          ))}
        </div>
        {isMobile ? (
          <>
            <button data-testid="go-to-btn" onClick={() => setGoOpen(true)}
              className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-[#E5DEC9] bg-white px-2.5 py-1.5 text-xs font-medium whitespace-nowrap text-[#1B3B2B] hover:bg-[#F0EAE1]"><CalendarDays className="h-4 w-4" />Vai a</button>
            <MobileSheet open={goOpen} onOpenChange={setGoOpen} title="Vai a una data" testId="go-to-sheet">
              <div className="p-2 overflow-y-auto flex justify-center">
                <EquiroCalendar mode="single" selected={new Date(selDay + "T00:00:00")} defaultMonth={new Date(selDay + "T00:00:00")}
                  onSelect={(d) => { if (d) { setSelDay(iso(d)); setGoOpen(false); } }} />
              </div>
            </MobileSheet>
          </>
        ) : (
          <Popover open={goOpen} onOpenChange={setGoOpen}>
            <PopoverTrigger asChild>
              <button data-testid="go-to-btn"
                className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-[#E5DEC9] bg-white px-3 py-1.5 text-sm font-medium whitespace-nowrap text-[#1B3B2B] hover:bg-[#F0EAE1]"><CalendarDays className="h-4 w-4" />Vai a</button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0" data-testid="go-to-pop">
              <EquiroCalendar mode="single" selected={new Date(selDay + "T00:00:00")} defaultMonth={new Date(selDay + "T00:00:00")}
                onSelect={(d) => { if (d) { setSelDay(iso(d)); setGoOpen(false); } }} />
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between gap-2">
        <Button variant="outline" size="icon" data-testid="cal-prev" onClick={() => shift(-1)}><ChevronLeft className="h-4 w-4" /></Button>
        <div className="text-center">
          <button data-testid="cal-today" onClick={() => setSelDay(iso(new Date()))} className="font-semibold text-[#1B3B2B] text-sm hover:text-[#8C532B]">Oggi</button>
          <p className="text-xs text-[#5F6B64] capitalize">{rangeLabel()}</p>
        </div>
        <Button variant="outline" size="icon" data-testid="cal-next" onClick={() => shift(1)}><ChevronRight className="h-4 w-4" /></Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button data-testid="toggle-cancelled" onClick={() => setShowCancelled((v) => !v)} className="flex items-center gap-1.5 text-xs text-[#8C532B] hover:underline">
          {showCancelled ? <><EyeOff className="h-3.5 w-3.5" />Nascondi annullate</> : <><Eye className="h-3.5 w-3.5" />Mostra annullate</>}
        </button>
        {view === "week" && closing.size > 0 && (
          <button data-testid="toggle-operative" onClick={() => setOperativeOnly((v) => !v)} className="flex items-center gap-1.5 text-xs text-[#8C532B] hover:underline">
            {operativeOnly ? "Mostra tutti i giorni" : "Solo giorni operativi"}
          </button>
        )}
        {instructors.length > 0 && (
          <Select value={instrFilter} onValueChange={setInstrFilter}>
            <SelectTrigger data-testid="instructor-filter" className="h-8 w-auto text-xs gap-1"><GraduationCap className="h-3.5 w-3.5" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli istruttori</SelectItem>
              {myInstructorId && <SelectItem value="mine">Le mie lezioni</SelectItem>}
              {instructors.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {instructors.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap text-[11px] text-[#5F6B64]" data-testid="instructor-legend">
            {instructors.filter((i) => i.active).map((i) => <span key={i.id} className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: i.color || instrColor(i.id) }} />{i.name}</span>)}
          </div>
        )}
        <button data-testid="save-default-view" onClick={saveDefaultView} className="text-xs text-[#8C532B] hover:underline">Imposta vista predefinita</button>
      </div>

      {occ === null ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#1B3B2B]" /></div>
      ) : view === "month" ? (
        <MonthView gridDays={gridDays} selMonth={new Date(selDay + "T00:00:00").getMonth()} occ={filteredOcc} closing={closing} weekend={weekend}
          specialMap={specialMap} onPickDay={(dIso) => { setSelDay(dIso); setView("day"); }} />
      ) : (
        <>
          {view === "day" && specialMap[selDay] && (
            <SpecialBanner sp={specialMap[selDay]} canWrite={perms.canWrite} testId="special-banner"
              hasLessons={byDay(selDay).some((o) => !o.is_cancelled)} onCancel={() => cancelDay(selDay)} onRestore={() => restoreDay(selDay)} />
          )}
          <div className="overflow-x-auto -mx-1 px-1" data-testid="matrix-scroll">
            <div className="grid gap-px bg-[#EFE8D6] rounded-xl overflow-hidden min-w-fit"
              style={{ gridTemplateColumns: colTemplate }} data-testid="planning-grid">
              <div className="bg-[#FAF8F5]" />
              {cols.map((d) => {
                const dIso = iso(d); const wd = wdOf(d); const isToday = dIso === iso(new Date());
                const isClose = closing.has(wd); const isWknd = weekend.has(wd); const sp = specialMap[dIso];
                return (
                  <button key={dIso} data-testid={`day-header-${dIso}`} onClick={() => { setSelDay(dIso); setView("day"); }}
                    className={`px-1.5 py-1.5 text-center transition-colors ${sp ? "bg-[#FBF3F6]" : isToday ? "bg-[#FFFBEB]" : isClose ? "bg-[#F2EFE8]" : isWknd ? "bg-[#F7F5EF]" : "bg-[#FAF8F5]"} hover:bg-[#F0EAE1]`}>
                    <p className="text-[10px] font-mono-label text-[#8C532B]">{WD[wd]}</p>
                    <p className={`font-semibold text-sm ${isToday ? "text-[#8C532B]" : "text-[#1B3B2B]"}`}>{d.getDate()}</p>
                    {isClose && <p className="text-[9px] text-[#9A8E76] font-medium">chiuso</p>}
                    {sp && <p data-testid={`special-${dIso}`} className="text-[9px] text-[#A03D62] font-semibold truncate flex items-center justify-center gap-0.5"><Sparkles className="h-2.5 w-2.5 shrink-0" />{sp.items[0].label}</p>}
                  </button>
                );
              })}
              {bands.length === 0 && (
                <div className="col-span-full bg-white py-10 text-center text-sm text-[#5F6B64]">Nessuno slot configurato né lezioni in questo periodo. Configura gli slot o crea una lezione.</div>
              )}
              {bands.map((b) => (
                <BandRow key={b.start} band={b} cols={cols} view={view} todayIso={iso(new Date())} closing={closing} weekend={weekend} canWrite={perms.canWrite} instrColorMap={instrColorMap}
                  itemsFor={(dIso) => byDayTime(dIso, b.start)} onNew={(dIso) => openNew(dIso, b)} onEdit={openAttendance} onRestore={restoreOcc} />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Lesson dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) { setDialog(null); setOrig(null); setAvail(null); setMakeRec(false); } }}>
        <DialogContent style={dlgStyle} className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="lesson-dialog">
          <DialogHeader><DialogTitle className="font-display">{orig ? "Modifica lezione" : "Nuova lezione"}</DialogTitle><DialogDescription className="sr-only">Dettagli della lezione: orario, colore, partecipanti e cavalli.</DialogDescription></DialogHeader>
          {dialog && (() => {
            const sorted = sortByName(dialog.participants, (p) => p.name || "");
            const ModeSel = !orig && (
              <div className="flex gap-2">
                {["single", "series"].map((m) => (
                  <button key={m} data-testid={`mode-${m}`} onClick={() => setDialog({ ...dialog, mode: m })}
                    className={`flex-1 rounded-lg border px-3 py-1.5 text-sm ${dialog.mode === m ? "border-[#1B3B2B] bg-[#1B3B2B] text-white" : "border-[#E5DEC9] text-[#1B3B2B]"}`}>{m === "single" ? "Singola" : "Ricorrente"}</button>
                ))}
              </div>
            );
            const Details = (
              <div className="space-y-3">
                {dialog.mode !== "series" && <div><Label>Data</Label><DateField testId="lesson-date" className="mt-1" value={dialog.date} onChange={(v) => setDialog({ ...dialog, date: v })} /></div>}
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Inizio</Label><Input type="time" data-testid="lesson-start" className="mt-1" value={dialog.start_time} onChange={(e) => changeStart(e.target.value)} /></div>
                  <div><Label>Fine</Label><Input type="time" data-testid="lesson-end" className="mt-1" value={dialog.end_time} onChange={(e) => setDialog({ ...dialog, end_time: e.target.value })} /></div>
                </div>
                <div><Label>Titolo / gruppo (opz.)</Label><Input data-testid="lesson-title" className="mt-1" placeholder="Es. Principianti (facoltativo)" value={dialog.title} onChange={(e) => setDialog({ ...dialog, title: e.target.value })} /></div>
                <div><Label>Istruttore</Label>
                  <div className="mt-1">
                    <PersonSearchCreate
                      context="instructor"
                      stableId={current.id}
                      allowCreate={canManage}
                      placeholder="Nessun istruttore · cerca…"
                      value={dialog.instructor_id ? { instructor_id: dialog.instructor_id, label: (instructors.find((i) => i.id === dialog.instructor_id)?.name) || dialog.instructor || "Istruttore" } : null}
                      onChange={(v) => {
                        setDialog((d) => ({ ...d, instructor_id: v?.instructor_id || "", instructor: v?.label || "" }));
                        if (v?.instructor_id && !instructors.some((i) => i.id === v.instructor_id)) refreshInstructors();
                      }}
                    />
                  </div>
                </div>
                <div><Label>Colore</Label><div className="flex flex-wrap gap-2 mt-1">{COLORS.map((c) => <button key={c} data-testid={`color-${c}`} onClick={() => setDialog({ ...dialog, color: c })} style={{ backgroundColor: c }} className={`h-7 w-7 rounded-full transition ${dialog.color === c ? "ring-2 ring-offset-2 ring-[#1B3B2B]" : ""}`} />)}</div></div>
                {arenas.length > 0 && <div><Label>Campo / Area</Label>
                  <Select value={dialog.arena_id || "none"} onValueChange={(v) => setDialog({ ...dialog, arena_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="lesson-arena" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Nessun campo</SelectItem>{arenas.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>}
              </div>
            );
            const Recurrence = (
              <div className="space-y-3">
                {orig && !orig.series_id && !makeRec && (
                  <button data-testid="make-recurring-btn" onClick={() => { setMakeRec(true); setDialog((d) => ({ ...d, recurrence: { ...d.recurrence, start_date: d.date, weekdays: [wdOf(new Date(d.date + "T00:00:00"))] } })); }}
                    className="flex items-center gap-1.5 text-sm text-[#8C532B] hover:underline"><Repeat className="h-4 w-4" />Rendi ricorrente</button>
                )}
                {showRec && (
                  <div className="rounded-lg border border-[#E5DEC9] bg-[#FCFAF6] p-2.5 space-y-2" data-testid="recurrence-panel">
                    <Label>Giorni della settimana</Label>
                    <div className="flex gap-1">{WD.map((w, n) => <button key={n} data-testid={`wd-${n}`} onClick={() => toggleWd(n)} className={`h-8 w-8 rounded-full text-xs font-semibold ${dialog.recurrence.weekdays.includes(n) ? "bg-[#8C532B] text-white" : "bg-white border border-[#E5DEC9] text-[#1B3B2B]"}`}>{w[0]}</button>)}</div>
                    <div><Label className="text-xs">Frequenza</Label>
                      <Select value={dialog.recurrence.interval > 4 ? "custom" : String(dialog.recurrence.interval)} onValueChange={(v) => setDialog({ ...dialog, recurrence: { ...dialog.recurrence, interval: v === "custom" ? Math.max(2, dialog.recurrence.interval) : Number(v) } })}>
                        <SelectTrigger data-testid="rec-freq" className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{FREQ_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {dialog.recurrence.interval > 4 && <div><Label className="text-xs">Ogni (settimane)</Label><Input type="number" min="2" data-testid="rec-interval" className="mt-1" value={dialog.recurrence.interval} onChange={(e) => setDialog({ ...dialog, recurrence: { ...dialog.recurrence, interval: Math.max(2, Number(e.target.value) || 2) } })} /></div>}
                    <p className="text-[11px] text-[#5F6B64] italic" data-testid="rec-preview">{recurrencePreview(dialog.recurrence.weekdays, dialog.recurrence.interval)}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-xs">Inizio</Label><DateField testId="rec-start" className="mt-1" value={dialog.recurrence.start_date} onChange={(v) => setDialog({ ...dialog, recurrence: { ...dialog.recurrence, start_date: v } })} /></div>
                      <div><Label className="text-xs">Fine (opz.)</Label><DateField testId="rec-end" className="mt-1" value={dialog.recurrence.end_date} onChange={(v) => setDialog({ ...dialog, recurrence: { ...dialog.recurrence, end_date: v } })} /></div>
                    </div>
                    <p className="text-[11px] text-[#8C6D4F]">Imposta una data di fine per terminare la ricorrenza senza cancellare le lezioni passate.</p>
                  </div>
                )}
              </div>
            );
            const Participants = (
              <div>
                <Label>Partecipanti (Allievo · Cavallo)</Label>
                <div className="space-y-2 mt-1.5">
                  {sorted.map((p, i) => (
                    <div key={p._k} data-testid={`participant-${i}`} className={`rounded-lg border p-2 space-y-1.5 ${p.kind === "prova" ? "border-[#6D28D9] bg-[#F5F0FF]" : "border-[#E5DEC9]"}`}>
                      <div className="flex gap-2 items-start">
                        <div className="flex-1 space-y-1.5">
                          <PersonSearchCreate context="student" stableId={current.id} allowCreate
                            placeholder="Allievo · cerca o crea…"
                            value={p.name ? { person_id: p.person_id || null, student_id: p._student_id || null, label: p.name } : null}
                            onChange={(v) => {
                              if (!v) { setP(p._k, { name: "", person_id: null, _student_id: null, _habitual_horse_id: null, _habitual_horse_name: null }); return; }
                              const patch = { name: v.label || "", person_id: v.person_id || null, _student_id: v.student_id || null,
                                _habitual_horse_id: v.habitual_horse_id || null, _habitual_horse_name: v.habitual_horse_name || null };
                              const habitualId = v.habitual_horse_id;
                              if (habitualId && !p.horse_id) {
                                const busy = (avail?.unavailable || []).some((h) => h.id === habitualId);
                                const used = dialog.participants.some((q) => q._k !== p._k && q.horse_id === habitualId);
                                if (busy || used) toast.info("Cavallo abituale occupato: scegli manualmente.");
                                else { patch.horse_id = habitualId; patch.horse_name = ""; }
                              }
                              setP(p._k, patch);
                            }} />
                          <HorsePicker testId={`p-horse-${i}`} participant={p} horses={horses} avail={avail}
                            others={dialog.participants.filter((q) => q._k !== p._k)} onChange={(patch) => setP(p._k, patch)} />
                          <FermoWarn testId={`p-fermo-warn-${i}`} horseId={p.horse_id} avail={avail} horses={horses} />
                          {p._student_id && (p._habitual_horse_id || (p.horse_id && p.horse_id !== p._habitual_horse_id)) && (
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                              {p.horse_id && p.horse_id !== p._habitual_horse_id && <button data-testid={`p-set-habitual-${i}`} onClick={() => setHabitual(p._k, p.horse_id)} className="text-[11px] text-[#8C532B] hover:underline inline-flex items-center gap-0.5"><Star className="h-3 w-3" />Imposta come abituale</button>}
                              {p._habitual_horse_id && <button data-testid={`p-reset-habitual-${i}`} onClick={() => setHabitual(p._k, null)} className="text-[11px] text-[#B91C1C] hover:underline">Rimuovi cavallo abituale{p._habitual_horse_name ? ` (${p._habitual_horse_name})` : ""}</button>}
                            </div>
                          )}
                        </div>
                        <button data-testid={`p-del-${i}`} onClick={() => delP(p._k)} className="shrink-0 text-red-500 px-1 pt-1"><X className="h-4 w-4" /></button>
                      </div>
                      <button data-testid={`p-kind-${i}`} onClick={() => setP(p._k, { kind: p.kind === "prova" ? "normale" : "prova" })}
                        className={`rounded-full px-3 py-0.5 text-xs font-semibold border ${p.kind === "prova" ? "bg-[#6D28D9] text-white border-[#6D28D9]" : "bg-white text-[#5F6B64] border-[#E5DEC9]"}`}>{p.kind === "prova" ? "PROVA" : "Normale"}</button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" data-testid="add-participant" onClick={addP}><Plus className="h-3.5 w-3.5 mr-1" />Aggiungi partecipante</Button>
                </div>
              </div>
            );
            const Notes = <div><Label>Note (opz.)</Label><Textarea data-testid="lesson-notes" className="mt-1" value={dialog.notes} onChange={(e) => setDialog({ ...dialog, notes: e.target.value })} /></div>;
            const partCount = dialog.participants.filter((p) => (p.name || "").trim()).length;
            const UsedHorses = (
              <div>
                <Label>Cavalli utilizzati dalla lezione ({(dialog.lesson_horses || []).length})</Label>
                <p className="text-[11px] text-[#8C7A5E] mb-1">{partCount} partecipant{partCount === 1 ? "e" : "i"} · {(dialog.lesson_horses || []).length} cavall{(dialog.lesson_horses || []).length === 1 ? "o" : "i"} utilizzat{(dialog.lesson_horses || []).length === 1 ? "o" : "i"}. Cavalli usati dall'attività ma non assegnati a un singolo binomio (occupano comunque il cavallo globalmente).</p>
                <UsedHorsesField testId="lesson-horses" value={dialog.lesson_horses} horses={horses} exclude={dialog.participants.map((p) => p.horse_id).filter(Boolean)} onChange={(ids) => setDialog((d) => ({ ...d, lesson_horses: ids }))} />
              </div>
            );
            return (
              <div className="space-y-3">
                {ModeSel}
                {orig ? <>{Participants}{UsedHorses}{Details}{Recurrence}{Notes}</> : <>{Details}{Recurrence}{Participants}{UsedHorses}{Notes}</>}
              </div>
            );
          })()}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {orig && perms.canWrite && !orig.is_cancelled && <Button variant="outline" data-testid="lesson-attendance" onClick={() => { const o = orig; setDialog(null); setOrig(null); setAttOcc(o); }} className="text-[#1B3B2B] border-[#1B3B2B]/30 hover:bg-[#F0EAE1]"><ClipboardCheck className="h-4 w-4 mr-2" />Presenze</Button>}
            {orig && <Button variant="outline" data-testid="lesson-duplicate" onClick={duplicate}><Copy className="h-4 w-4 mr-2" />Duplica</Button>}
            {orig && <Button variant="outline" data-testid="lesson-delete" onClick={onDelete} className="text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="h-4 w-4 mr-2" />Elimina</Button>}
            <Button onClick={onSave} disabled={saving} data-testid="lesson-save" className="bg-[#1B3B2B] hover:bg-[#142E1F]">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{makeRec ? "Rendi ricorrente" : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Smart Conflict — same-instructor overlap on NEW lesson creation */}
      <Dialog open={!!conflictAsk} onOpenChange={(o) => !o && !saving && setConflictAsk(null)}>
        <DialogContent style={dlgStyle} className="max-h-[92vh] overflow-y-auto sm:max-w-md" data-testid="conflict-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-[#B45309]" />Conflitto istruttore</DialogTitle>
            <DialogDescription className="sr-only">Conflitto di orario per lo stesso istruttore nella stessa data.</DialogDescription>
          </DialogHeader>
          {conflictAsk && (() => {
            const ex = conflictAsk.exact_matches || [];
            const ov = conflictAsk.overlaps || [];
            const summaryLine = (c) => (c.participants || []).map((p) => (p.name || "—") + (p.horse_name ? ` · ${p.horse_name}` : "")).join(", ") || "Nessun partecipante";
            const CardBody = (c, i, kind) => (
              <div key={c.occ_key} data-testid={`conflict-${kind}-${i}`} className="rounded-lg border border-[#E5DEC9] bg-[#FCFAF6] p-2.5">
                <p className="text-xs font-semibold text-[#1B3B2B]">{c.title}{c.is_recurring ? " · ricorrente" : ""} · {c.start_time}–{c.end_time}</p>
                <p className="text-[11px] text-[#5F6B64] mt-0.5 break-words">{summaryLine(c)}</p>
                {kind === "exact" && ex.length > 1 && <Button size="sm" variant="outline" data-testid={`conflict-merge-${i}`} disabled={saving} onClick={() => doMerge(c)} className="mt-2 text-[#1B3B2B] border-[#1B3B2B]/30">Aggiungi a questa lezione</Button>}
              </div>
            );
            if (ex.length) {
              return (
                <div className="space-y-3" data-testid="conflict-exact-body">
                  <p className="text-sm text-[#2D3832]" data-testid="conflict-msg">
                    <strong>{ex[0].instructor_name}</strong> ha già una lezione dalle <strong>{ex[0].start_time}</strong> alle <strong>{ex[0].end_time}</strong>.
                  </p>
                  <div className="space-y-2">{ex.map((c, i) => CardBody(c, i, "exact"))}</div>
                  <div className="flex flex-col gap-2 pt-1">
                    {ex.length === 1 && <Button data-testid="conflict-merge" disabled={saving} onClick={() => doMerge(ex[0])} className="bg-[#1B3B2B] hover:bg-[#142E1F]">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Aggiungi alla lezione esistente</Button>}
                    <Button variant="outline" data-testid="conflict-create-anyway" disabled={saving} onClick={doCreateAnyway}>Crea comunque una nuova lezione</Button>
                    <Button variant="ghost" data-testid="conflict-cancel" disabled={saving} onClick={() => setConflictAsk(null)} className="text-[#5F6B64]">Annulla</Button>
                  </div>
                </div>
              );
            }
            return (
              <div className="space-y-3" data-testid="conflict-overlap-body">
                <p className="text-sm text-[#2D3832]" data-testid="conflict-msg">
                  <strong>{ov[0]?.instructor_name}</strong> risulta già impegnato dalle <strong>{ov[0]?.start_time}</strong> alle <strong>{ov[0]?.end_time}</strong>.
                </p>
                <div className="space-y-2">{ov.map((c, i) => CardBody(c, i, "overlap"))}</div>
                <div className="flex flex-col gap-2 pt-1">
                  <Button data-testid="conflict-create-anyway" disabled={saving} onClick={doCreateAnyway} className="bg-[#1B3B2B] hover:bg-[#142E1F]">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Crea comunque</Button>
                  <Button variant="ghost" data-testid="conflict-cancel" disabled={saving} onClick={() => setConflictAsk(null)} className="text-[#5F6B64]">Annulla</Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!scopeAsk} onOpenChange={(o) => !o && setScopeAsk(null)}>
        <DialogContent data-testid="scope-dialog">
          <DialogHeader><DialogTitle className="font-display">{scopeAsk === "delete" ? "Elimina lezione ricorrente" : "Modifica lezione ricorrente"}</DialogTitle><DialogDescription className="sr-only">Ambito dell'operazione sulla serie ricorrente.</DialogDescription></DialogHeader>
          <p className="text-sm text-[#5F6B64]">Questa lezione fa parte di una serie ricorrente. A cosa vuoi applicare l'operazione?</p>
          <p className="text-xs text-[#8C6D4F] mt-1" data-testid="scope-hint">Le presenze già registrate restano sempre salvate. Per non modificare le lezioni passate scegli «Questa e le successive».</p>
          <div className="grid gap-2 mt-2">{[["occurrence", "Solo questa lezione"], ["following", "Questa e le successive"], ["series", "Tutta la serie"]].map(([s, l]) => (
            <Button key={s} variant="outline" data-testid={`scope-${s}`} disabled={saving} onClick={() => scopeAsk === "delete" ? doDelete(s) : doSave(s)} className="justify-start">{l}</Button>
          ))}</div>
        </DialogContent>
      </Dialog>

      <FindSlotsDialog open={findOpen} onClose={() => setFindOpen(false)} stableId={current?.id} horses={horses} instructors={instructors}
        onPick={(slot, horseIds) => { setFindOpen(false); openNew(slot.date, { start: slot.start, end: slot.end, color: slot.color || COLORS[0] }, { instructor_id: slot.instructor_id || "", participants: horseIds.map((hid) => ({ _k: crypto.randomUUID?.() || Math.random().toString(36), name: "", horse_id: hid, horse_name: "", kind: "normale", notes: "" })) }); }} />

      <SlotsDialog open={setOpen} onClose={() => setSetOpen(false)} settings={settings} setSettings={setSettings} stableId={current?.id} onSaved={load}
        members={[]} onInstructorsChanged={() => api.get(`/stables/${current.id}/instructors`).then((r) => setInstructors(r.data || [])).catch(() => {})}
        onArenasChanged={() => api.get(`/stables/${current.id}/arenas`).then((r) => setArenas(r.data || [])).catch(() => {})} />

      <AttendanceDialog open={!!attOcc} onClose={() => setAttOcc(null)} stableId={current?.id} occ={attOcc} horses={horses} canWrite={perms.canWrite}
        onManage={(o) => { setAttOcc(null); openEdit(o); }} onChanged={load} />
      <RegistroDialog open={regOpen} onClose={() => setRegOpen(false)} stableId={current?.id} horses={horses} instructors={instructors} />
      <AbsencesDialog open={absOpen} onClose={() => { setAbsOpen(false); load(); }} stableId={current?.id} />
    </div>
  );
}

function BandRow({ band, cols, view, todayIso, closing, weekend, canWrite, instrColorMap, itemsFor, onNew, onEdit, onRestore }) {
  return (
    <>
      <div className="bg-[#FAF8F5] px-1 py-1.5 text-right">
        <p className="text-[11px] font-semibold text-[#1B3B2B] tabular-nums leading-none">{band.start}</p>
        <p className="text-[9px] text-[#9A8E76] tabular-nums">{band.end}</p>
      </div>
      {cols.map((d) => {
        const dIso = iso(d); const wd = wdOf(d); const items = itemsFor(dIso);
        const isToday = view === "week" && dIso === todayIso;
        const tint = isToday ? "bg-[#FFFBEB]" : closing.has(wd) ? "bg-[#F5F2EB]" : weekend.has(wd) ? "bg-[#FBFAF6]" : "bg-white";
        if (items.length === 0)
          return <button key={dIso} data-testid={`cell-${dIso}-${band.start}`} onClick={() => canWrite && onNew(dIso)}
            className={`${tint} min-h-[46px] w-full group ${canWrite ? "hover:bg-[#F0EAE1]" : ""}`}>
            {canWrite && <Plus className="h-3.5 w-3.5 mx-auto text-transparent group-hover:text-[#8C532B]" />}
          </button>;
        const multi = items.length > 1 && cols.length > 1;
        return (
          <div key={dIso} className={`${tint} p-1 flex gap-1 ${multi ? "flex-row flex-nowrap" : "flex-col"}`}>
            {items.map((o) => (
              <div key={o.id} className={multi ? "flex-1 min-w-[132px]" : "w-full"}>
                <LessonBlock o={o} accent={instrColorMap[o.instructor_id]} onClick={() => onEdit(o)} onRestore={() => onRestore(o)} />
              </div>
            ))}
          </div>
        );
      })}
    </>
  );
}

function MonthView({ gridDays, selMonth, occ, closing, weekend, specialMap, onPickDay }) {
  const countByDay = useMemo(() => { const m = {}; (occ || []).forEach((o) => { if (o.is_cancelled) return; (m[o.date] = m[o.date] || []).push(o.color || "#1B3B2B"); }); return m; }, [occ]);
  return (
    <div data-testid="month-grid">
      <div className="grid grid-cols-7 gap-px mb-px">{WD.map((w) => <div key={w} className="text-center text-[10px] font-mono-label text-[#8C532B] py-1">{w}</div>)}</div>
      <div className="grid grid-cols-7 gap-px bg-[#EFE8D6] rounded-xl overflow-hidden">
        {gridDays.map((d) => {
          const dIso = iso(d); const wd = wdOf(d); const inMonth = d.getMonth() === selMonth; const isToday = dIso === iso(new Date());
          const colorsList = countByDay[dIso] || []; const sp = specialMap[dIso]; const isClose = closing.has(wd); const isWknd = weekend.has(wd);
          return (
            <button key={dIso} data-testid={`month-day-${dIso}`} onClick={() => onPickDay(dIso)}
              className={`min-h-[64px] p-1.5 text-left transition-colors ${!inMonth ? "bg-[#F7F5EF] opacity-50" : sp ? "bg-[#FBF3F6]" : isClose ? "bg-[#F2EFE8]" : isWknd ? "bg-[#FBFAF6]" : "bg-white"} hover:bg-[#F0EAE1]`}>
              <div className="flex items-center justify-between">
                <span className={`text-xs font-semibold ${isToday ? "bg-[#8C532B] text-white rounded-full h-5 w-5 flex items-center justify-center" : "text-[#1B3B2B]"}`}>{d.getDate()}</span>
                {colorsList.length > 0 && <span className="text-[10px] font-semibold text-[#5F6B64]">{colorsList.length}</span>}
              </div>
              {sp && <p className="text-[8px] text-[#A03D62] font-semibold truncate mt-0.5">{sp.items[0].label}</p>}
              <div className="flex flex-wrap gap-0.5 mt-1">{colorsList.slice(0, 4).map((c, i) => <span key={i} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: c }} />)}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SpecialBanner({ sp, hasLessons, onCancel, onRestore, canWrite, testId }) {
  return (
    <div data-testid={testId} className="rounded-xl border border-[#C99AA6] bg-[#FBF3F6] px-3 py-2.5">
      <p className="text-sm font-semibold text-[#A03D62] flex items-center gap-1.5"><Sparkles className="h-4 w-4" />{sp.items.map((x) => x.label).join(" · ")}</p>
      {canWrite && (
        <div className="flex gap-2 mt-2">
          {hasLessons && <Button size="sm" variant="outline" data-testid="special-cancel-day" onClick={onCancel} className="text-[#A03D62] border-[#C99AA6]">Annulla lezioni del giorno</Button>}
          <Button size="sm" variant="ghost" data-testid="special-restore-day" onClick={onRestore} className="text-[#5F6B64]"><RotateCcw className="h-3.5 w-3.5 mr-1" />Ripristina</Button>
        </div>
      )}
    </div>
  );
}

function LessonBlock({ o, onClick, onRestore, accent }) {
  const bg = o.color || "#1B3B2B";
  const fg = textOn(bg);
  const parts = sortByName(o.participants || [], (p) => p.name || "");
  const partIds = new Set(parts.filter((p) => p.horse_id).map((p) => p.horse_id));
  const extraHorses = (o.lesson_horse_names || []).filter((x) => !partIds.has(x.id));
  if (o.is_cancelled) {
    return (
      <button data-testid={`lesson-${o.id}`} onClick={onClick} className="w-full text-left rounded-lg px-2 py-1.5 border border-dashed border-[#C9BDA0] bg-[#F1EDE4] opacity-80 hover:opacity-100 transition">
        <p className="text-[11px] font-semibold text-[#8C7A5E] tabular-nums">{o.start_time}–{o.end_time}</p>
        <p className="text-[11px] font-semibold text-[#8C7A5E] line-through">Lezione annullata</p>
      </button>
    );
  }
  return (
    <button data-testid={`lesson-${o.id}`} onClick={onClick} className="w-full h-full text-left rounded-lg overflow-hidden flex hover:brightness-105 transition" style={{ backgroundColor: bg, color: fg }}>
      {accent && (
        <div className="flex-none flex items-center justify-center py-1" style={{ backgroundColor: accent, width: o.instructor_name ? 16 : 4 }} data-testid={o.instructor_name ? `instr-side-${o.id}` : undefined}>
          {o.instructor_name && <span className="text-white text-[9px] font-semibold leading-none whitespace-nowrap" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>{o.instructor_name}</span>}
        </div>
      )}
      <div className="flex-1 min-w-0 px-1.5 py-1">
        <div className="flex items-center gap-1">
          <span className="font-semibold text-[11px] tabular-nums">{o.start_time}–{o.end_time}</span>
          {o.series_id && (
            <span data-testid={`lesson-kind-${o.id}`} title="Ricorrente" className="inline-flex items-center opacity-70 ml-0.5">
              <Repeat className="h-2.5 w-2.5" />
            </span>
          )}
          {o.has_prova && <span data-testid={`prova-marker-${o.id}`} title="Contiene una prova" className="ml-auto inline-flex items-center gap-1 rounded-full bg-white/95 pl-1 pr-1.5 py-[1px] shadow-sm"><span className="eq-prova-glow h-2 w-2 rounded-full" style={{ backgroundColor: PROVA_INK }} /><span className="text-[9px] font-extrabold tracking-wide" style={{ color: PROVA_INK }}>PROVA</span></span>}
        </div>
        {o.title && <p data-testid={`lesson-title-${o.id}`} title={o.title} className="text-xs font-extrabold leading-tight truncate mt-0.5">{o.title}</p>}
        <div className="mt-1 space-y-1.5">
          {parts.map((p, i) => {
            const plannedHorse = p.horse_id ? (p.horse_name || "—") : (p.horse_name || null);
            const actualHorse = p.actual_horse_name || null;
            const isAbsent = p.att_status === "absent" || !!p.absent_planned;
            const changed = p.att_status === "present" && actualHorse && plannedHorse && actualHorse !== plannedHorse;
            const prova = p.kind === "prova";
            const fermo = !!p.planned_op_block;
            const fermoIcon = fermo ? <AlertTriangle data-testid={`planned-fermo-${o.id}-${i}`} title="Cavallo pianificato temporaneamente fermo in questa data" className="inline-block h-2.5 w-2.5 ml-0.5 align-[-1px] text-[#B45309]" /> : null;
            return (
              <div key={i} data-testid={prova ? `prova-binomio-${o.id}-${i}` : undefined}
                className={`break-words ${prova ? "rounded bg-white/95 px-1 py-0.5" : ""} ${isAbsent ? "opacity-40" : ""}`}
                style={prova ? { color: PROVA_INK } : undefined}>
                <p className={`text-[11px] font-semibold leading-tight break-words ${isAbsent ? "line-through" : ""}`}>{p.name || "—"}</p>
                {p.absent_planned && p.att_status !== "absent" && <p data-testid={`lesson-absent-${o.id}-${i}`} className="text-[9px] leading-tight opacity-70 no-underline">assenza programmata</p>}
                {changed ? (
                  <>
                    <p data-testid={`lesson-actual-horse-${o.id}-${i}`} className="text-[10px] leading-tight break-words font-medium">{actualHorse}</p>
                    <p className="text-[9px] leading-tight break-words opacity-70">previsto: {plannedHorse}{fermoIcon}</p>
                  </>
                ) : (
                  (actualHorse || plannedHorse) && <p className={`text-[10px] leading-tight break-words ${prova ? "" : "opacity-80"}`}>{actualHorse || plannedHorse}{fermoIcon}</p>
                )}
              </div>
            );
          })}
          {extraHorses.length > 0 && <p data-testid={`lesson-horses-line-${o.id}`} className="text-[10px] leading-tight break-words opacity-90 mt-0.5">{extraHorses.length === 1 ? "Cavallo: " : "Cavalli: "}{extraHorses.map((x) => x.name).join(" · ")}</p>}
          {parts.length === 0 && extraHorses.length === 0 && <p className="text-[11px] opacity-80">Lezione</p>}
        </div>
        {o.attendance_status === "verified" && <p data-testid={`att-status-${o.id}`} className="mt-1 text-[9px] font-semibold opacity-90">✓ Presenze verificate</p>}
        {o.attendance_status === "partial" && <p data-testid={`att-status-${o.id}`} className="mt-1 text-[9px] font-semibold opacity-80">Presenze da completare</p>}
      </div>
    </button>
  );
}

function UsedHorsesField({ value, horses, onChange, testId, exclude = [] }) {
  const [q, setQ] = useState(""); const [open, setOpen] = useState(false);
  const ids = value || [];
  const chosen = ids.map((id) => horses.find((h) => h.id === id)).filter(Boolean);
  const filtered = horses.filter((h) => !ids.includes(h.id) && !(exclude || []).includes(h.id)).filter((h) => !q.trim() || matchHorse({ horse_name: h.display_name, horse_official_name: h.official_name, horse_microchip: h.microchip, horse_ueln: h.ueln }, q));
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {chosen.map((h) => (
          <span key={h.id} data-testid={`${testId}-chip-${h.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#EDE7D6] text-[#1B3B2B] text-xs px-2 py-0.5">
            {h.display_name}
            <button type="button" data-testid={`${testId}-remove-${h.id}`} onClick={() => onChange(ids.filter((x) => x !== h.id))} className="text-[#8C6D4F] hover:text-[#B91C1C]"><X className="h-3 w-3" /></button>
          </span>
        ))}
        {chosen.length === 0 && <span className="text-xs text-[#8C7A5E]">Nessun cavallo di lezione</span>}
      </div>
      <div className="relative">
        <Input data-testid={`${testId}-input`} value={q} placeholder="Aggiungi cavallo utilizzato (nome, microchip, UELN)…" autoComplete="off"
          onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 180)} />
        {open && (
          <div className="absolute z-50 mt-1 w-full max-h-48 overflow-auto rounded-lg border border-[#E5DEC9] bg-white shadow-lg">
            {filtered.length === 0 && <p className="px-3 py-2 text-xs text-[#8C6D4F]">Nessun cavallo</p>}
            {filtered.slice(0, 30).map((h) => (
              <button key={h.id} type="button" data-testid={`${testId}-opt-${h.id}`} onMouseDown={(e) => { e.preventDefault(); onChange([...ids, h.id]); setQ(""); setOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-[#F0EAE1] text-[#1B3B2B]">{h.display_name}{(h.official_name && h.official_name !== h.display_name) ? <span className="text-[11px] text-[#8C6D4F]"> · {h.official_name}</span> : ""}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StudentPicker({ value, onPick, stableId, testId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!open || !stableId) return;
    let active = true;
    const t = setTimeout(() => { api.get(`/stables/${stableId}/students`, { params: { q: value || "" } }).then((r) => { if (active) setItems(r.data || []); }).catch(() => {}); }, 180);
    return () => { active = false; clearTimeout(t); };
  }, [value, open, stableId]);
  return (
    <div className="relative">
      <Input data-testid={testId} value={value || ""} placeholder="Nome allievo" autoComplete="off"
        onChange={(e) => { onPick(e.target.value, null, null); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {open && items.length > 0 && (
        <div data-testid={`${testId}-list`} className="absolute z-50 mt-1 w-full max-h-52 overflow-auto rounded-lg border border-[#E5DEC9] bg-white shadow-lg">
          {items.map((s) => (
            <button key={s.id} type="button" data-testid={`${testId}-opt-${s.id}`} onMouseDown={(e) => { e.preventDefault(); onPick(s.name, s.habitual_horse_id, s); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[#F0EAE1] text-[#1B3B2B]">{s.name}{s.habitual_horse_name && <span className="text-[11px] text-[#8C6D4F]"> · di solito {s.habitual_horse_name}</span>}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// Date-aware operational-block for an ALREADY-ASSIGNED horse (reuses the availability payload,
// which is fetched for the lesson's date → the warning only shows when the date falls in the period).
function opBlockFor(avail, horseId) {
  if (!avail || !horseId) return null;
  for (const arr of [avail.unavailable, avail.available_other, avail.available]) {
    const h = (arr || []).find((x) => x.id === horseId);
    if (h && h.op_block) return h.op_block;
  }
  return null;
}

function FermoWarn({ horseId, avail, horses, testId }) {
  const op = opBlockFor(avail, horseId);
  if (!op) return null;
  const name = horses.find((h) => h.id === horseId)?.display_name || "Il cavallo";
  const rientro = op.expected_return ? `Rientro previsto: ${op.expected_return}`
    : (op.prognosis_min_date && op.prognosis_max_date) ? `Rientro previsto: ${op.prognosis_min_date} – ${op.prognosis_max_date}`
    : op.prognosis_max_date ? `Rientro previsto entro: ${op.prognosis_max_date}` : "";
  const what = op.status === "ritirato" ? "risulta ritirato dall'attività" : "risulta temporaneamente fermo in questa data";
  return (
    <div data-testid={testId} className="rounded-md bg-[#FCF3E2] border border-[#F0D9A8] px-2.5 py-1.5 text-[11px] text-[#8A5A00] flex items-start gap-1.5">
      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
      <span>Attenzione: {name} {what}. Puoi comunque confermarlo.{rientro ? ` ${rientro}` : ""}</span>
    </div>
  );
}

function HorsePicker({ participant, others, horses, avail, onChange, testId, flat }) {
  const assigned = new Set(others.map((p) => p.horse_id).filter(Boolean));
  const busy = {}; const usableFree = new Set(); const otherFree = new Set(); const opMap = {};
  if (avail) {
    (avail.unavailable || []).forEach((h) => { busy[h.id] = h.conflicts?.[0]; if (h.op_block) opMap[h.id] = h.op_block; });
    (avail.available || []).forEach((h) => usableFree.add(h.id));
    (avail.available_other || []).forEach((h) => { otherFree.add(h.id); if (h.op_block) opMap[h.id] = h.op_block; });
  }
  const ownerMatch = (h) => participant.name && h.owner_name && norm(h.owner_name) === norm(participant.name);
  const label = participant.horse_id ? (horses.find((h) => h.id === participant.horse_id)?.display_name || participant.horse_name || "—") : (participant.horse_name || "");
  const pick = (h, close) => { onChange({ horse_id: h.id, horse_name: "" }); close(); };
  const external = (name, close) => { onChange({ horse_id: "", horse_name: name }); close(); };
  return (
    <SmartSearchPicker testId={testId} placeholder="Cerca cavallo (nome, microchip, UELN)…"
      displayLabel={label} mobileTitle="Cavallo effettivo" flat={flat}
      onClear={(participant.horse_id || participant.horse_name) ? () => onChange({ horse_id: "", horse_name: "" }) : undefined}>
      {(q, { close, optionProps }) => {
        const filtered = horses.filter((h) => !assigned.has(h.id)).filter((h) => matchHorse({ horse_name: h.display_name, horse_official_name: h.official_name, horse_microchip: h.microchip, horse_ueln: h.ueln }, q));
        // Priority: owner's own horse -> free & school pool -> other free -> not-school -> busy
        const rank = (h) => busy[h.id] ? 5 : ownerMatch(h) ? 0 : usableFree.has(h.id) ? 1 : (avail ? (otherFree.has(h.id) ? 3 : 1) : (h.usable_in_lessons === false ? 3 : 1));
        filtered.sort((a, b) => rank(a) - rank(b) || a.display_name.localeCompare(b.display_name));
        const trimmed = q.trim();
        const exact = horses.some((h) => h.display_name.toLowerCase() === trimmed.toLowerCase());
        return (
          <>
            <button type="button" {...optionProps(() => external("", close))} className="w-full text-left px-3 py-2 text-sm text-[#5F6B64] hover:bg-[#F0EAE1] border-b border-[#F0EAE1]">Nessun cavallo</button>
            {filtered.slice(0, 20).map((h) => {
              const b = busy[h.id]; const notSchool = h.usable_in_lessons === false; const owner = ownerMatch(h); const op = opMap[h.id];
              const opTxt = op ? (op.status === "ritirato" ? "ritirato dall'attività"
                : `indisponibile${op.reason ? ` (${op.reason})` : ""}${op.expected_return ? ` · rientro ${op.expected_return}` : (op.prognosis_max_date ? ` · rientro entro ${op.prognosis_max_date}` : "")}`) : null;
              return (
                <button key={h.id} type="button" data-testid={`${testId}-opt-${h.id}`} disabled={!!b} {...optionProps(() => { if (b) return; pick(h, close); })}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ${b ? "opacity-60 cursor-not-allowed" : "hover:bg-[#F0EAE1]"} text-[#1B3B2B]`}>
                  <span className="truncate">{h.display_name}{owner && <span className="text-[10px] text-[#8C532B] font-semibold"> · del proprietario</span>}{notSchool && !owner && !b && !op && <span className="text-[10px] text-[#8C6D4F]"> · non da scuola</span>}{op && <span data-testid={`${testId}-op-${h.id}`} className="text-[10px] text-[#B45309] font-medium"> · {opTxt}</span>}</span>
                  {b ? <span className="text-[10px] text-[#B91C1C] shrink-0">già impegnato {b.time}</span> : (usableFree.has(h.id) || owner) && <span className="text-[10px] text-[#2E7D32] shrink-0">libero</span>}
                </button>
              );
            })}
            {trimmed && !exact && <button type="button" data-testid={`${testId}-external`} {...optionProps(() => external(trimmed, close))} className="w-full text-left px-3 py-2 text-sm text-[#8C532B] hover:bg-[#F0EAE1] border-t border-[#F0EAE1] font-medium">+ Usa «{trimmed}» come cavallo esterno</button>}
          </>
        );
      }}
    </SmartSearchPicker>
  );
}

const ST_LABEL = { unrecorded: "Non registrato", present: "Presente", absent: "Assente" };

export function AttendanceDialog({ open, onClose, stableId, occ, horses, canWrite, onManage, onChanged }) {
  const dlgStyle = useMobileDialogStyle();
  const [entries, setEntries] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [variations, setVariations] = useState(null); // Area 6: pending changes summary
  const [pendingList, setPendingList] = useState(null);
  const [conflictMsg, setConflictMsg] = useState(null); // Area 5: blocking same-horse conflict
  const [dupConfirm, setDupConfirm] = useState(null);   // anti-duplicate: detached consuntivo overlap
  const [moveEntry, setMoveEntry] = useState(null);     // "Sposta" participant target
  const [removeEntry, setRemoveEntry] = useState(null); // "Rimuovi solo da questa lezione" target
  const [removing, setRemoving] = useState(false);
  const [avail, setAvail] = useState(null);
  const initialRef = useRef({}); // session snapshot: only NEW/CHANGED rows are reported post-save
  const snapKey = (e) => e.student_id || ("n:" + (e.name || "").trim().toLowerCase());
  const snapVal = (e) => ({ status: e.status || "unrecorded", ah: e.actual_horse_id || null,
    ahn: (e.actual_horse_name || "").trim().toLowerCase(), kind: e.kind || "normale" });

  useEffect(() => {
    if (!open || !occ || !stableId) { setAvail(null); return; }
    api.get(`/stables/${stableId}/calendar/availability?date=${occ.date}&start=${occ.start_time}&end=${occ.end_time}&exclude=${occ.series_id || occ.id}`)
      .then((r) => setAvail(r.data)).catch(() => setAvail(null));
  }, [open, occ, stableId]);  // eslint-disable-line

  useEffect(() => {
    if (!open || !occ || !stableId) return;
    setLoading(true);
    api.get(`/stables/${stableId}/calendar/attendance`, { params: { occ_key: occ.id } })
      .then((r) => {
        const d = r.data.attendance || r.data.template;
        setCancelled(!!(r.data.cancelled || r.data.attendance?.occ_cancelled));
        setMeta({ occ_key: occ.id, series_id: occ.series_id || null, date: occ.date,
          start_time: occ.start_time, end_time: occ.end_time,
          instructor_id: occ.instructor_id || null, instructor_name: occ.instructor_name || null });
        const loaded = sortByName(d.entries || [], (e) => e.name || "").map((e) => ({ ...e }));
        setEntries(loaded);
        const snap = {};
        for (const e of loaded) snap[snapKey(e)] = snapVal(e);
        initialRef.current = snap;
      })
      .catch((e) => { toast.error(errText(e.response?.data?.detail)); onClose(); })
      .finally(() => setLoading(false));
  }, [open, occ, stableId]);  // eslint-disable-line

  const doCancel = async () => {
    setBusy(true);
    try { await api.post(`/stables/${stableId}/calendar/occurrence/cancel`, { occ_key: occ.id }); toast.success("Lezione annullata"); onChanged && onChanged(); onClose(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doRestore = async () => {
    setBusy(true);
    try { await api.post(`/stables/${stableId}/calendar/occurrence/restore`, { occ_key: occ.id }); toast.success("Lezione ripristinata"); onChanged && onChanged(); onClose(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const setStatus = (i, status) => setEntries((es) => es.map((e, j) => {
    if (j !== i) return e;
    if (status === "present") {
      const has = e.actual_horse_id || e.actual_horse_name;
      return { ...e, status, actual_horse_id: has ? e.actual_horse_id : (e.planned_horse_id || null),
        actual_horse_name: has ? e.actual_horse_name : (e.planned_horse_name || null) };
    }
    if (status === "absent") return { ...e, status, actual_horse_id: null, actual_horse_name: null };
    return { ...e, status };
  }));
  const patch = (i, p) => setEntries((es) => es.map((e, j) => j === i ? { ...e, ...p } : e));
  const confirmAll = () => {
    const list = entries.map((e) => {
      const has = e.actual_horse_id || e.actual_horse_name;
      return { ...e, status: "present", actual_horse_id: has ? e.actual_horse_id : (e.planned_horse_id || null),
        actual_horse_name: has ? e.actual_horse_name : (e.planned_horse_name || null) };
    });
    setEntries(list);
    validateAndSave(list);  // one-tap: set all present, but still validate variations/conflicts
  };

  const horseLabel = (e) => e.actual_horse_id ? (horses.find((h) => h.id === e.actual_horse_id)?.display_name || "—") : (e.actual_horse_name || null);
  const hkey = (id, name) => id || ((name || "").trim() ? "n:" + (name || "").trim().toLowerCase() : null);
  // Area 5: two PRESENT participants on the same actual horse => blocking conflict.
  const computeConflict = (list) => {
    const seen = {};
    for (const e of list) {
      if (e.status !== "present") continue;
      const k = hkey(e.actual_horse_id, e.actual_horse_name);
      if (!k) continue;
      if (seen[k]) return horseLabel(e) || "Il cavallo";
      seen[k] = true;
    }
    return null;
  };
  // Area 6: significant variations vs the plan (absences, actual≠planned, reassigned absent horse).
  const computeVariations = (list) => {
    const msgs = [];
    const absentByHorse = {};
    for (const e of list) {
      if (e.status === "absent") {
        const pk = hkey(e.planned_horse_id, e.planned_horse_name);
        if (pk) absentByHorse[pk] = e.name || "—";
      }
    }
    const reassigned = new Set();
    for (const e of list) {
      if (e.status !== "present") continue;
      const ak = hkey(e.actual_horse_id, e.actual_horse_name);
      const pk = hkey(e.planned_horse_id, e.planned_horse_name);
      if (ak && ak !== pk) {
        const an = horseLabel(e);
        if (absentByHorse[ak]) {
          msgs.push(`${e.name} ha utilizzato ${an}, inizialmente previsto per ${absentByHorse[ak]}.`);
          reassigned.add(ak);
        } else {
          msgs.push(`${e.name} ha utilizzato ${an}${e.planned_horse_name ? ` (previsto ${e.planned_horse_name})` : ""}.`);
        }
      }
    }
    for (const e of list) {
      if (e.status !== "absent") continue;
      const pk = hkey(e.planned_horse_id, e.planned_horse_name);
      if (pk && reassigned.has(pk)) continue;
      msgs.push(`${e.name} è assente.`);
    }
    return msgs;
  };
  const validateAndSave = (list) => {
    const conflict = computeConflict(list);
    if (conflict) { setConflictMsg(conflict); return; }
    const vars = computeVariations(list);
    if (vars.length) { setPendingList(list); setVariations(vars); return; }
    save(list);
  };
  // Added from the Attendance screen = an operational (consuntivo) event: mark Present and
  // set the chosen horse as the ACTUAL horse (planned stays empty for an on-the-fly pair).
  const addEntry = (p) => setEntries((es) => sortByName([...es, {
    student_id: p.student_id || null, name: p.name || "",
    planned_horse_id: null, planned_horse_name: null,
    planned_external: false,
    actual_horse_id: p.horse_id || null, actual_horse_name: p.horse_id ? null : (p.horse_name || null),
    kind: p.kind || "normale", status: "present", note: null,
  }], (e) => e.name || ""));

  const save = async (entriesArg, confirmDuplicate = false) => {
    if (saving) return;
    const list = Array.isArray(entriesArg) ? entriesArg : entries;
    setSaving(true);
    try {
      // LOTTO A: horses changed on NOT-yet-confirmed rows are CURRENT PLANNED changes -> persist
      // them in ONE atomic occurrence-override call. The server validates the FINAL state (so an
      // A<->B swap is allowed) and rejects a same-occurrence duplicate before any write.
      const plannedChanges = list.filter((en) => en.status !== "present" && en._planned_dirty)
        .map((en) => ({ student_id: en.student_id || null, name: en.name,
          horse_id: en.planned_horse_id || null, horse_name: en.planned_horse_id ? null : (en.planned_horse_name || null) }));
      if (plannedChanges.length) {
        await api.put(`/stables/${stableId}/calendar/occurrence/participants-horses`, { occ_key: occ.id, changes: plannedChanges });
      }
      const r = await api.put(`/stables/${stableId}/calendar/attendance`, { ...meta, confirm_duplicate: confirmDuplicate,
        entries: list.map((e) => ({ student_id: e.student_id || null, name: e.name,
          planned_horse_id: e.planned_horse_id || null, planned_horse_name: e.planned_horse_name || null,
          planned_external: !!e.planned_external,
          actual_horse_id: e.actual_horse_id || null,
          actual_horse_name: e.actual_horse_id ? null : (e.actual_horse_name || null),
          kind: e.kind || "normale", status: e.status || "unrecorded", note: e.note || null })) });
      if (r.data && r.data.needs_confirm) { setDupConfirm({ list, students: r.data.duplicate_students || [], message: r.data.message }); return; }
      // Re-sync entries from the server so Presenze reflects the exact CURRENT PLANNED just written
      // (single source of truth shared with the Calendar card).
      try {
        const rr = await api.get(`/stables/${stableId}/calendar/attendance`, { params: { occ_key: occ.id } });
        const dd = rr.data.attendance || rr.data.template;
        const reloaded = sortByName(dd.entries || [], (e) => e.name || "").map((e) => ({ ...e }));
        setEntries(reloaded);
        const snap2 = {}; for (const e of reloaded) snap2[snapKey(e)] = snapVal(e); initialRef.current = snap2;
      } catch { /* non-blocking */ }
      // Incremental confirmation: report ONLY rows that are NEW or CHANGED vs the snapshot
      // captured when the dialog opened (unchanged participants are excluded).
      const changed = [];
      for (const e of list) {
        const prev = initialRef.current[snapKey(e)];
        const cur = snapVal(e);
        if (!prev) changed.push({ name: e.name || "—", isNew: true });
        else if (prev.status !== cur.status || prev.ah !== cur.ah || prev.ahn !== cur.ahn || prev.kind !== cur.kind)
          changed.push({ name: e.name || "—", isNew: false });
      }
      if (changed.length === 0) {
        toast.success("Presenze salvate", { description: "Nessuna modifica in questa sessione." });
      } else {
        const names = changed.slice(0, 6).map((c) => c.name + (c.isNew ? " (nuovo)" : "")).join(", ");
        const more = changed.length > 6 ? ` e altri ${changed.length - 6}` : "";
        toast.success(`Presenze salvate · ${changed.length} ${changed.length === 1 ? "aggiornamento" : "aggiornamenti"}`,
          { description: names + more });
      }
      onChanged && onChanged(); onClose();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const handleMove = (e) => {
    if (e.status === "present" || e.status === "absent") { toast.error("Ha già un consuntivo in questa lezione: gestisci prima le presenze."); return; }
    setMoveEntry(e);
  };
  const handleRemove = (e) => {
    if (e.status === "present" || e.status === "absent") { toast.error("Ha già un consuntivo in questa lezione: gestisci prima le presenze."); return; }
    setRemoveEntry(e);
  };
  const doRemoveParticipant = async () => {
    if (!removeEntry) return;
    setRemoving(true);
    try {
      await api.post(`/stables/${stableId}/calendar/occurrence/remove-participant`, { occ_key: occ.id, student_id: removeEntry.student_id || null, name: removeEntry.name });
      toast.success("Rimosso solo da questa lezione"); onChanged && onChanged(); onClose();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setRemoving(false); setRemoveEntry(null); }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={dlgStyle} className="p-0 gap-0 flex flex-col max-h-[92vh] sm:max-w-lg" data-testid="attendance-dialog">
        <div className="px-5 pt-5 pb-3 border-b border-[#EFE8D6] shrink-0">
          <DialogTitle className="font-display text-lg">Presenze</DialogTitle>
          <DialogDescription className="text-xs text-[#5F6B64] mt-0.5">
            {occ && `${fmtLong(occ.date)} · ${occ.start_time}–${occ.end_time}${occ.instructor_name ? " · " + occ.instructor_name : ""}`}
          </DialogDescription>
          {canWrite && (
            <div className="absolute right-11 top-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="attendance-actions-menu" className="h-8 w-8 inline-flex items-center justify-center rounded-md text-[#5F6B64] hover:bg-[#F0EAE1]"><MoreVertical className="h-4 w-4" /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {onManage && <DropdownMenuItem data-testid="attendance-manage" onClick={() => onManage(occ)}><Settings2 className="h-4 w-4 mr-2" />Gestisci lezione</DropdownMenuItem>}
                  {cancelled
                    ? <DropdownMenuItem data-testid="attendance-restore-menu" disabled={busy} onClick={doRestore} className="text-[#2E7D32]"><RotateCcw className="h-4 w-4 mr-2" />Ripristina lezione</DropdownMenuItem>
                    : <DropdownMenuItem data-testid="attendance-cancel-lesson" disabled={busy} onClick={doCancel} className="text-[#B91C1C] focus:text-[#B91C1C]"><X className="h-4 w-4 mr-2" />Annulla lezione</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {loading ? <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#8C532B]" /></div> : (
          <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2" data-testid="attendance-list">
            {!cancelled && (
              <div className="rounded-lg bg-[#F7F4EE] border border-[#EFE8D6] px-3 py-2 text-[11px] text-[#5F6B64]" data-testid="attendance-help">
                <p>La <span className="font-semibold text-[#1B3B2B]">pianificazione</span> indica chi era previsto. Le <span className="font-semibold text-[#1B3B2B]">Presenze</span> registrano ciò che è realmente avvenuto.</p>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="inline-flex items-center gap-1">Non registrato <InfoHint testId="help-non-registrato" label="Cosa significa Non registrato" text="La presenza non è ancora stata verificata. Non significa Assente." /></span>
                  <span className="inline-flex items-center gap-1">Assente <InfoHint testId="help-assente" label="Quando usare Assente" text="Usa Assente solo se l'allievo era effettivamente previsto ma non ha partecipato." /></span>
                </div>
              </div>
            )}
            {cancelled && (
              <div data-testid="attendance-cancelled-banner" className="rounded-lg border border-dashed border-[#C9BDA0] bg-[#F1EDE4] px-3 py-2 flex items-center gap-2">
                <span className="text-sm font-semibold text-[#8C7A5E]">Lezione annullata</span>
                {canWrite && <Button size="sm" variant="ghost" data-testid="attendance-restore" disabled={busy} onClick={doRestore} className="ml-auto h-8 text-[#2E7D32]"><RotateCcw className="h-3.5 w-3.5 mr-1" />Ripristina</Button>}
              </div>
            )}
            {!cancelled && <Button data-testid="attendance-confirm-all" onClick={confirmAll} className="w-full bg-[#1B3B2B] hover:bg-[#142E1F] h-10">
              <Check className="h-4 w-4 mr-2" />Conferma tutti presenti
            </Button>}
            {entries.length === 0 && <p className="text-sm text-[#5F6B64] text-center py-4">Nessun partecipante in questa lezione.</p>}
            {entries.map((e, i) => (
              <AttendanceRow key={i} e={e} i={i} cancelled={cancelled} horses={horses} avail={avail}
                others={entries.filter((x, j) => j !== i && x.status === "present").map((x) => ({ horse_id: x.actual_horse_id }))}
                onStatus={setStatus} onPatch={patch} onMove={() => handleMove(e)} onRemove={() => handleRemove(e)} />
            ))}
            {!cancelled && (
              <Button variant="outline" data-testid="attendance-add-binomio" onClick={() => setAddOpen(true)}
                className="w-full h-10 border-dashed border-[#C9BDA0] text-[#1B3B2B] hover:bg-[#F0EAE1]">
                <Plus className="h-4 w-4 mr-2" />Aggiungi binomio
              </Button>
            )}
          </div>
        )}

        {!cancelled && !loading && (
          <div className="shrink-0 border-t border-[#EFE8D6] bg-white px-5 py-3" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
            <Button onClick={() => validateAndSave(entries)} disabled={saving} data-testid="attendance-save" className="w-full h-11 bg-[#1B3B2B] hover:bg-[#142E1F]">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salva presenze</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
    <AddBinomioSheet open={addOpen} onClose={() => setAddOpen(false)} stableId={stableId} occ={occ} horses={horses}
      others={entries.filter((e) => e.status === "present").map((e) => ({ horse_id: e.actual_horse_id }))} onAdd={addEntry} />

    {/* Area 6 — variations summary before saving */}
    <Dialog open={!!variations} onOpenChange={(o) => { if (!o) { setVariations(null); setPendingList(null); } }}>
      <DialogContent data-testid="attendance-variations-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Variazioni rispetto alla lezione</DialogTitle>
          <DialogDescription className="text-xs text-[#5F6B64]">Controlla le differenze rispetto alla pianificazione prima di salvare.</DialogDescription>
        </DialogHeader>
        <ul className="space-y-1.5 text-sm text-[#2D3832]">
          {(variations || []).map((m, i) => (
            <li key={i} data-testid={`attendance-variation-${i}`} className="flex gap-2"><span className="text-[#8C532B]">•</span><span>{m}</span></li>
          ))}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setVariations(null); setPendingList(null); }} data-testid="attendance-variations-cancel">Annulla</Button>
          <Button onClick={() => { const l = pendingList; setVariations(null); setPendingList(null); save(l); }} disabled={saving}
            data-testid="attendance-variations-confirm" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Conferma e salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Area 5 — blocking same-horse conflict */}
    <Dialog open={!!conflictMsg} onOpenChange={(o) => !o && setConflictMsg(null)}>
      <DialogContent data-testid="attendance-conflict-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#B91C1C]">Binomio da correggere</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#2D3832]" data-testid="attendance-conflict-msg">
          {conflictMsg} risulta utilizzato da due partecipanti presenti. Correggi il binomio prima di salvare.
        </p>
        <DialogFooter>
          <Button onClick={() => setConflictMsg(null)} data-testid="attendance-conflict-ok" className="bg-[#1B3B2B] hover:bg-[#142E1F]">Ho capito</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Anti-duplicate — a detached historical consuntivo already exists for this slot */}
    <Dialog open={!!dupConfirm} onOpenChange={(o) => { if (!o) setDupConfirm(null); }}>
      <DialogContent data-testid="attendance-duplicate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#8C532B]">Storico presenze già esistente</DialogTitle>
          <DialogDescription className="text-xs text-[#5F6B64]">{dupConfirm?.message}</DialogDescription>
        </DialogHeader>
        {(dupConfirm?.students || []).length > 0 && (
          <p className="text-sm text-[#2D3832]" data-testid="attendance-duplicate-students">
            Interessati: <span className="font-semibold">{(dupConfirm.students || []).join(", ")}</span>
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setDupConfirm(null)} data-testid="attendance-duplicate-cancel">Annulla</Button>
          <Button onClick={() => { const l = dupConfirm.list; setDupConfirm(null); save(l, true); }} disabled={saving}
            data-testid="attendance-duplicate-confirm" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Registra comunque
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* D — Rimuovi solo da questa lezione (confirm) */}
    <Dialog open={!!removeEntry} onOpenChange={(o) => { if (!o) setRemoveEntry(null); }}>
      <DialogContent data-testid="remove-participant-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Rimuovi solo da questa lezione</DialogTitle>
          <DialogDescription className="text-xs text-[#5F6B64]">
            {removeEntry?.name} non risulterà più previsto in questa lezione. Non viene creata un'assenza e le occorrenze successive della serie restano invariate.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setRemoveEntry(null)} data-testid="remove-participant-cancel">Annulla</Button>
          <Button onClick={doRemoveParticipant} disabled={removing} data-testid="remove-participant-confirm" className="bg-[#B91C1C] hover:bg-[#991616]">
            {removing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Rimuovi da questa lezione
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* C — Sposta partecipante in un'altra occorrenza */}
    <MoveParticipantDialog open={!!moveEntry} onClose={() => setMoveEntry(null)} stableId={stableId} occ={occ} entry={moveEntry} horses={horses}
      onDone={() => { setMoveEntry(null); onChanged && onChanged(); onClose(); }} />
    </>
  );
}

function MoveParticipantDialog({ open, onClose, stableId, occ, entry, horses, onDone }) {
  const dlgStyle = useMobileDialogStyle();
  const [date, setDate] = useState(occ?.date || "");
  const [dests, setDests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toKey, setToKey] = useState(null);
  const [horse, setHorse] = useState({ horse_id: "", horse_name: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open || !entry) return;
    setDate(occ?.date || "");
    setToKey(null);
    setHorse({ horse_id: entry.planned_horse_id || "", horse_name: entry.planned_horse_id ? "" : (entry.planned_horse_name || "") });
  }, [open, occ, entry]);  // eslint-disable-line
  useEffect(() => {
    if (!open || !stableId || !date || !occ) return;
    setLoading(true); setToKey(null);
    api.get(`/stables/${stableId}/calendar`, { params: { start: date, end: date } })
      .then((r) => setDests((r.data || []).filter((o) => !o.is_cancelled && o.id !== occ.id)))
      .catch(() => setDests([])).finally(() => setLoading(false));
  }, [open, stableId, date, occ]);  // eslint-disable-line
  const submit = async () => {
    if (!toKey) { toast.error("Seleziona la lezione di destinazione"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/stables/${stableId}/calendar/occurrence/move-participant`, {
        from_occ_key: occ.id, to_occ_key: toKey, student_id: entry.student_id || null, name: entry.name,
        planned_horse_id: horse.horse_id || null, planned_horse_name: horse.horse_id ? null : (horse.horse_name || null),
      });
      if ((r.data.conflicts || []).length) toast.warning("Spostato. Attenzione: possibile conflitto cavallo/istruttore nella destinazione.");
      else toast.success("Partecipante spostato");
      onDone && onDone();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={dlgStyle} className="sm:max-w-md" data-testid="move-participant-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">Sposta partecipante</DialogTitle>
          <DialogDescription className="text-xs text-[#5F6B64]">
            {entry?.name} viene spostato solo per questa data. La serie ricorrente resta invariata e non viene creata un'assenza.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-[#5F6B64]">Giorno destinazione</Label>
            <DateField testId="move-dest-date" className="mt-1" value={date} onChange={setDate} />
          </div>
          <div>
            <Label className="text-xs text-[#5F6B64]">Lezione destinazione</Label>
            {loading ? <div className="py-4 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-[#8C532B]" /></div> : (
              <div className="mt-1 space-y-1 max-h-48 overflow-auto" data-testid="move-dest-list">
                {dests.length === 0 && <p className="text-xs text-[#5F6B64] py-2">Nessuna altra lezione in questo giorno. Crea prima la lezione, poi sposta.</p>}
                {dests.map((o) => (
                  <button key={o.id} type="button" data-testid={`move-dest-${o.id}`} onClick={() => setToKey(o.id)}
                    className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${toKey === o.id ? "border-[#1B3B2B] bg-[#F1F8F2]" : "border-[#E5DEC9] bg-white hover:bg-[#F0EAE1]"}`}>
                    <p className="text-sm font-semibold text-[#1B3B2B] tabular-nums">{o.start_time}–{o.end_time}{o.title ? ` · ${o.title}` : ""}</p>
                    <p className="text-[11px] text-[#5F6B64]">{o.instructor_name ? `${o.instructor_name} · ` : ""}{(o.participants || []).length} allievi</p>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs text-[#5F6B64]">Cavallo previsto (destinazione)</Label>
            <div className="mt-1"><HorsePicker testId="move-planned-horse"
              participant={{ name: entry?.name, horse_id: horse.horse_id, horse_name: horse.horse_name }}
              others={[]} horses={horses} avail={null} onChange={setHorse} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="move-participant-cancel">Annulla</Button>
          <Button onClick={submit} disabled={saving || !toKey} data-testid="move-participant-submit" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Sposta qui
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddBinomioSheet({ open, onClose, stableId, occ, horses, others, onAdd }) {
  const dlgStyle = useMobileDialogStyle();
  const [name, setName] = useState("");
  const [studentId, setStudentId] = useState(null);
  const [horse, setHorse] = useState({ horse_id: "", horse_name: "" });
  const [prova, setProva] = useState(false);
  const [avail, setAvail] = useState(null);
  useEffect(() => {
    if (!open || !occ || !stableId) return;
    setName(""); setStudentId(null); setHorse({ horse_id: "", horse_name: "" }); setProva(false); setAvail(null);
    api.get(`/stables/${stableId}/calendar/availability?date=${occ.date}&start=${occ.start_time}&end=${occ.end_time}&exclude=${occ.series_id || occ.id}`)
      .then((r) => setAvail(r.data)).catch(() => setAvail(null));
  }, [open, occ, stableId]);  // eslint-disable-line
  const submit = async () => {
    if (!name.trim() && !horse.horse_id && !horse.horse_name) { toast.error("Inserisci un allievo o un cavallo"); return; }
    // Persist the binomio to THIS specific lesson/occurrence (standalone -> the single lesson;
    // recurring -> only that occurrence via an exception override, never the series/template) AND
    // record it in the Consuntivo. The horse is not blocked by another rider's PLANNED horse.
    try {
      const r = await api.post(`/stables/${stableId}/calendar/occurrence/add-participant`, {
        occ_key: occ.id,
        participant: { name: name.trim(), student_id: studentId, horse_id: horse.horse_id || null,
          horse_name: horse.horse_id ? null : (horse.horse_name || null), kind: prova ? "prova" : "normale" },
      });
      onAdd(r.data.participant);
      if ((r.data.conflicts || []).length) toast.warning("Attenzione: il cavallo risulta impegnato in un'altra lezione");
      toast.success("Binomio aggiunto"); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Impossibile aggiungere il binomio"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={dlgStyle} className="sm:max-w-sm" data-testid="add-binomio-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">Aggiungi binomio</DialogTitle>
          <DialogDescription className="text-xs text-[#5F6B64]">Aggiunto solo a questa lezione, non alle occorrenze future.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-[#5F6B64]">Allievo</Label>
            <div className="mt-1"><StudentPicker testId="add-binomio-student" value={name} stableId={stableId}
              onPick={(n, habitualId) => { setName(n); if (habitualId && !horse.horse_id) setHorse({ horse_id: habitualId, horse_name: "" }); setStudentId(null); }} /></div>
          </div>
          <div>
            <Label className="text-xs text-[#5F6B64]">Cavallo</Label>
            <div className="mt-1"><HorsePicker testId="add-binomio-horse"
              participant={{ name, horse_id: horse.horse_id, horse_name: horse.horse_name }}
              others={others} horses={horses} avail={avail} onChange={setHorse} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#1B3B2B]"><Switch data-testid="add-binomio-prova" checked={prova} onCheckedChange={setProva} />PROVA</label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="add-binomio-cancel">Annulla</Button>
          <Button onClick={submit} data-testid="add-binomio-submit" className="bg-[#1B3B2B] hover:bg-[#142E1F]">Aggiungi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Compact "Cambia cavallo" control: a pencil next to the horse name that opens the picker
// DIRECTLY onto the searchable list — Popover on desktop, bottom Drawer on mobile (keyboard-safe).
function HorseEdit({ i, entry, plannedName, horses, others, onChange, isMobile, mode }) {
  const [open, setOpen] = useState(false);
  const picker = (
    <HorsePicker flat testId={`attendance-horse-${i}`}
      participant={{ name: entry.name, horse_id: entry.actual_horse_id || "", horse_name: entry.actual_horse_name || "" }}
      others={others || []} horses={horses} avail={null}
      onChange={(p) => { onChange(p); setOpen(false); }} />
  );
  const trigger = (onClick) => (mode === "empty"
    ? <button type="button" data-testid={`attendance-edit-horse-${i}`} onClick={onClick}
        className="text-[11px] text-[#8C532B] font-medium inline-flex items-center gap-1 hover:underline"><Pencil className="h-3 w-3" />Scegli cavallo</button>
    : <button type="button" data-testid={`attendance-edit-horse-${i}`} aria-label="Cambia cavallo" onClick={onClick}
        className="shrink-0 inline-flex items-center justify-center h-5 w-5 rounded text-[#8C6D4F] hover:text-[#1B3B2B] hover:bg-[#F0EAE1]"><Pencil className="h-3.5 w-3.5" /></button>);
  if (isMobile) {
    return (
      <>
        {trigger(() => setOpen(true))}
        <MobileSheet open={open} onOpenChange={(o) => { if (!o) setOpen(false); }} title="Cavallo effettivo"
          testId={`attendance-horse-${i}-sheet`}>
          <p className="px-4 pb-2 text-[11px] text-[#8C6D4F] shrink-0">Solo il cavallo montato oggi in questa lezione. La pianificazione resta invariata.</p>
          <div className="flex-1 min-h-0 overflow-y-auto px-4" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
            {picker}
            {plannedName && <p className="text-[11px] text-[#8C6D4F] mt-2">Pianificato: {plannedName}</p>}
          </div>
        </MobileSheet>
      </>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger()}</PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-3 space-y-1" data-testid={`attendance-horse-pop-${i}`}>
        <Label className="text-[11px] font-semibold text-[#1B3B2B]">Cavallo effettivo</Label>
        <p className="text-[10px] text-[#8C6D4F] mb-1">Solo il cavallo montato oggi in questa lezione. La pianificazione del Calendario resta invariata.</p>
        {picker}
        {plannedName && <p className="text-[11px] text-[#8C6D4F] mt-1">Pianificato: {plannedName}</p>}
      </PopoverContent>
    </Popover>
  );
}

function AttendanceRow({ e, i, cancelled, horses, avail, others, onStatus, onPatch, onMove, onRemove }) {
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const actualName = e.actual_horse_id ? (horses.find((h) => h.id === e.actual_horse_id)?.display_name || "—") : (e.actual_horse_name || null);
  // Resolve the planned horse name from its id too, so a locally-picked CURRENT PLANNED horse
  // (id set, name not yet returned by the server) shows immediately under the participant.
  const plannedName = e.planned_horse_id ? (horses.find((h) => h.id === e.planned_horse_id)?.display_name || e.planned_horse_name || "—") : (e.planned_horse_name || null);
  const changed = !!(actualName && plannedName && actualName !== plannedName);
  const shownHorse = actualName || plannedName;
  const SEG = [["unrecorded", "—"], ["present", "Presente"], ["absent", "Assente"]];
  const segCls = (s, sel) => {
    if (cancelled) return "opacity-50 cursor-not-allowed bg-white text-[#9A8E76]";
    if (!sel) return "bg-white text-[#5F6B64] hover:bg-[#F0EAE1]";
    return s === "present" ? "bg-[#2E7D5B] text-white" : s === "absent" ? "bg-[#B91C1C] text-white" : "bg-[#5F6B64] text-white";
  };
  // Compact by default. The ACTUAL horse is changed by tapping the pencil next to the horse name,
  // which opens the picker directly (Popover desktop / Drawer mobile). No permanent input, no menu.
  const canPickHorse = !cancelled && e.status !== "absent";
  const isPresent = e.status === "present";
  // For a NOT-YET-CONFIRMED row the picker edits the CURRENT PLANNED horse (occurrence override);
  // once Present it edits the ACTUAL mount. Show current planned as the selected value pre-confirm.
  const pickerEntry = isPresent ? e
    : { ...e, actual_horse_id: e.planned_horse_id || null, actual_horse_name: e.planned_horse_id ? null : (e.planned_horse_name || null) };
  const origName = e.original_horse_name || null;
  const plannedFromOrig = !isPresent && origName && (e.planned_horse_name || null) && origName !== e.planned_horse_name;
  const editProps = { i, entry: pickerEntry, plannedName, horses, others, isMobile,
    onChange: isPresent
      ? (p) => onPatch(i, { actual_horse_id: p.horse_id || null, actual_horse_name: p.horse_id ? null : (p.horse_name || null) })
      : (p) => onPatch(i, { planned_horse_id: p.horse_id || null, planned_horse_name: p.horse_id ? null : (p.horse_name || null), _planned_dirty: true }) };
  return (
    <div data-testid={`attendance-entry-${i}`} className={`rounded-lg border px-2.5 py-2 ${cancelled ? "border-[#E5DEC9] bg-[#F7F4EE] opacity-70" : e.status === "present" ? "border-[#BFD9C4] bg-[#F1F8F2]" : e.status === "absent" ? "border-[#E5C2C2] bg-[#FBF3F3]" : "border-[#E5DEC9] bg-white"}`}>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1" data-testid={`attendance-binomio-${i}`}>
          <p className="font-semibold text-[#1B3B2B] text-[15px] leading-tight break-words">
            {e.name || "—"}
            {e.kind === "prova" && <span data-testid={`attendance-prova-${i}`} className="ml-1 rounded-full px-1.5 py-[1px] text-[9px] font-extrabold align-middle" style={{ backgroundColor: "#F0E9FB", color: PROVA_INK }}>PROVA</span>}
          </p>
          {shownHorse ? (
            <p className="text-xs text-[#8C6D4F] leading-tight flex items-center gap-1 min-w-0">
              <span className={`truncate ${changed ? "font-medium text-[#2E7D5B]" : ""}`}>{shownHorse}</span>
              {canPickHorse && <HorseEdit {...editProps} mode="edit" />}
            </p>
          ) : (canPickHorse && <div className="mt-0.5"><HorseEdit {...editProps} mode="empty" /></div>)}
          {changed && <p data-testid={`attendance-planned-note-${i}`} className="text-[11px] text-[#8C6D4F] leading-tight truncate">Pianificato: {plannedName}</p>}
          {plannedFromOrig && <p data-testid={`attendance-original-note-${i}`} className="text-[11px] text-[#8C6D4F] leading-tight truncate">Pianificato originariamente: {origName}</p>}
        </div>
        <div className="shrink-0 flex rounded-lg border border-[#E5DEC9] overflow-hidden h-9" data-testid={`attendance-status-${i}`}>
          {SEG.map(([s, l], idx) => (
            <button key={s} title={l} data-testid={`attendance-status-${i}-${s}`} disabled={cancelled} onClick={() => onStatus(i, s)}
              className={`w-9 flex items-center justify-center text-sm font-semibold transition-colors ${idx > 0 ? "border-l border-[#E5DEC9]" : ""} ${segCls(s, e.status === s)}`}>
              {s === "present" ? <Check className="h-4 w-4" /> : s === "absent" ? <X className="h-4 w-4" /> : <span>—</span>}
            </button>
          ))}
        </div>
        {!cancelled && (
          <Popover open={menuOpen} onOpenChange={setMenuOpen}>
            <PopoverTrigger asChild>
              <button data-testid={`attendance-row-menu-${i}`} className="relative shrink-0 h-9 w-8 inline-flex items-center justify-center rounded-md text-[#5F6B64] hover:bg-[#F0EAE1]">
                <MoreVertical className="h-4 w-4" />
                {e.note && <span data-testid={`attendance-note-dot-${i}`} className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-[#C4611A]" />}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 p-3 space-y-3">
              <div>
                <Label className="text-[11px] text-[#5F6B64] flex items-center gap-1"><StickyNote className="h-3 w-3" />Nota</Label>
                <Textarea data-testid={`attendance-note-${i}`} className="mt-1 text-sm" rows={2} placeholder="Nota (opz.)"
                  value={e.note || ""} onChange={(ev) => onPatch(i, { note: ev.target.value })} />
              </div>
              <div className="border-t border-[#F0EAE1] pt-2 space-y-1">
                <button data-testid={`attendance-move-${i}`} onClick={() => { setMenuOpen(false); onMove && onMove(); }}
                  className="w-full flex items-center gap-2 text-left text-sm text-[#1B3B2B] hover:bg-[#F0EAE1] rounded-md px-2 py-1.5"><ArrowRightLeft className="h-3.5 w-3.5 text-[#8C532B]" />Sposta in un'altra lezione</button>
                {e.added_occasional
                  ? <button data-testid={`attendance-remove-${i}`} onClick={() => { setMenuOpen(false); onRemove && onRemove(); }}
                      className="w-full flex items-center gap-2 text-left text-sm text-[#B91C1C] hover:bg-[#FBF3F3] rounded-md px-2 py-1.5"><UserMinus className="h-3.5 w-3.5" />Rimuovi da questa lezione</button>
                  : <p data-testid={`attendance-standard-hint-${i}`} className="text-[11px] text-[#8C6D4F] px-2 py-1">Partecipante fisso: se oggi non c'è, segnalo <b>Assente</b>.</p>}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
      {!cancelled && e.status === "present" && e.actual_horse_id && (
        <FermoWarn testId={`attendance-fermo-warn-${i}`} horseId={e.actual_horse_id} avail={avail} horses={horses} />
      )}
      {cancelled && e.note && <p className="text-[11px] text-[#8C532B] italic mt-1">{e.note}</p>}
    </div>
  );
}

function AbsencesDialog({ open, onClose, stableId }) {
  const dlgStyle = useMobileDialogStyle();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [view, setView] = useState("active");
  const load = () => {
    if (!stableId) return;
    setLoading(true);
    api.get(`/stables/${stableId}/calendar/absences`).then((r) => setList(r.data || [])).catch(() => setList([])).finally(() => setLoading(false));
  };
  useEffect(() => { if (open) { setForm(null); setView("active"); load(); } }, [open, stableId]);  // eslint-disable-line
  const save = async () => {
    if (!form?.name?.trim()) { toast.error("Seleziona un allievo"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Inserisci le date"); return; }
    try {
      if (form.id) await api.put(`/stables/${stableId}/calendar/absences/${form.id}`, { start_date: form.start_date, end_date: form.end_date, reason: form.reason || null });
      else await api.post(`/stables/${stableId}/calendar/absences`, { name: form.name.trim(), student_id: form.student_id || null, start_date: form.start_date, end_date: form.end_date, reason: form.reason || null });
      toast.success("Assenza salvata"); setForm(null); load();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/stables/${stableId}/calendar/absences/${id}`); toast.success("Assenza rimossa"); load(); } catch { toast.error("Errore"); } };
  const endToday = async (a) => {
    const t = iso(new Date());
    try { await api.put(`/stables/${stableId}/calendar/absences/${a.id}`, { end_date: t < a.start_date ? a.start_date : t }); toast.success("Assenza interrotta"); load(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={dlgStyle} className="sm:max-w-lg" data-testid="absences-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">Assenze allievi</DialogTitle>
          <DialogDescription className="text-xs text-[#5F6B64]">Periodi di assenza (ferie, malattia, viaggio, sospensione…). Nelle lezioni del periodo l'allievo risulta assente e il suo cavallo si libera, senza modificare le serie ricorrenti.</DialogDescription>
        </DialogHeader>
        {form ? (
          <div className="space-y-3" data-testid="absence-form">
            <div>
              <Label className="text-xs text-[#5F6B64]">Allievo</Label>
              {form.id ? <p className="text-sm font-semibold text-[#1B3B2B] mt-1">{form.name}</p>
                : <div className="mt-1"><StudentPicker testId="absence-student" value={form.name} stableId={stableId} onPick={(n) => setForm((f) => ({ ...f, name: n, student_id: null }))} /></div>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-[#5F6B64]">Dal</Label><DateField testId="absence-start" className="mt-1" value={form.start_date} onChange={(v) => setForm((f) => ({ ...f, start_date: v }))} /></div>
              <div><Label className="text-xs text-[#5F6B64]">Al</Label><DateField testId="absence-end" className="mt-1" value={form.end_date} onChange={(v) => setForm((f) => ({ ...f, end_date: v }))} /></div>
            </div>
            <div><Label className="text-xs text-[#5F6B64]">Motivo (opz.)</Label><Input data-testid="absence-reason" className="mt-1" value={form.reason || ""} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="ferie, malattia…" /></div>
            <div className="flex gap-2 justify-end"><Button variant="outline" onClick={() => setForm(null)} data-testid="absence-cancel">Annulla</Button><Button onClick={save} data-testid="absence-save" className="bg-[#1B3B2B] hover:bg-[#142E1F]">Salva</Button></div>
          </div>
        ) : (
          <div className="space-y-2">
            <Button onClick={() => setForm({ name: "", student_id: null, start_date: iso(new Date()), end_date: iso(new Date()), reason: "" })} data-testid="absence-new-btn" className="w-full bg-[#1B3B2B] hover:bg-[#142E1F]"><Plus className="h-4 w-4 mr-2" />Nuova assenza</Button>
            {(() => {
              const today = iso(new Date());
              const dm = (s) => `${s.slice(8, 10)}/${s.slice(5, 7)}`;
              const inCorso = (a) => a.start_date <= today && today <= a.end_date;
              const active = list.filter((a) => a.end_date >= today).sort((a, b) => {
                const ac = inCorso(a), bc = inCorso(b);
                if (ac !== bc) return ac ? -1 : 1;
                return a.start_date < b.start_date ? -1 : a.start_date > b.start_date ? 1 : 0;
              });
              const history = list.filter((a) => a.end_date < today).sort((a, b) => a.end_date < b.end_date ? 1 : a.end_date > b.end_date ? -1 : 0);
              const shown = view === "active" ? active : history;
              const badge = (a) => {
                if (inCorso(a)) return a.end_date === today
                  ? { t: "Termina oggi", bg: "#FBF0E4", ink: "#8C532B" }
                  : { t: "In corso", bg: "#E6F1EA", ink: "#2E7D5B" };
                return { t: `Dal ${dm(a.start_date)}`, bg: "#F0EAE1", ink: "#5F6B64" };
              };
              return (
                <>
                  <div className="flex rounded-lg border border-[#E5DEC9] overflow-hidden" data-testid="absence-view-tabs">
                    {[["active", "Attive e programmate"], ["history", "Storico"]].map(([k, l]) => (
                      <button key={k} data-testid={`absence-view-${k}`} onClick={() => setView(k)}
                        className={`flex-1 text-xs font-semibold py-1.5 transition-colors ${view === k ? "bg-[#1B3B2B] text-white" : "bg-white text-[#5F6B64] hover:bg-[#F0EAE1]"}`}>{l}</button>
                    ))}
                  </div>
                  {loading ? <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#8C532B]" /></div> : (
                    <div className="space-y-1.5 max-h-72 overflow-auto" data-testid="absence-list">
                      {shown.length === 0 && <p className="text-xs text-[#5F6B64] py-6 text-center" data-testid={`absence-empty-${view}`}>{view === "active" ? "Nessuna assenza attiva o programmata." : "Nessuna assenza passata."}</p>}
                      {shown.map((a) => {
                        const b = view === "active" ? badge(a) : null;
                        return (
                          <div key={a.id} data-testid={`absence-item-${a.id}`} className="rounded-lg border border-[#E5DEC9] bg-white px-3 py-2">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-[#1B3B2B] truncate">{a.name}</p>
                                <p className="text-[11px] text-[#5F6B64] flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                                  {b && <span data-testid={`absence-badge-${a.id}`} className="rounded-full px-1.5 py-[1px] text-[10px] font-semibold whitespace-nowrap" style={{ backgroundColor: b.bg, color: b.ink }}>{b.t}</span>}
                                  <span className="tabular-nums">{a.start_date} → {a.end_date}{a.reason ? ` · ${a.reason}` : ""}</span>
                                </p>
                              </div>
                              <div className="flex flex-wrap justify-end gap-1">
                                <button data-testid={`absence-edit-${a.id}`} onClick={() => setForm({ id: a.id, name: a.name, student_id: a.student_id, start_date: a.start_date, end_date: a.end_date, reason: a.reason || "" })} className="text-xs px-2 py-1 rounded-md border border-[#E5DEC9] hover:bg-[#F0EAE1]">Modifica</button>
                                {view === "active" && <button data-testid={`absence-interrupt-${a.id}`} onClick={() => endToday(a)} className="text-xs px-2 py-1 rounded-md border border-[#E5DEC9] hover:bg-[#F0EAE1]">Interrompi</button>}
                                <button data-testid={`absence-delete-${a.id}`} onClick={() => del(a.id)} className="text-xs px-2 py-1 rounded-md border border-[#E5C2C2] text-[#B91C1C] hover:bg-[#FBF3F3]">Elimina</button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const REG_MODES = [["week", "Settimana"], ["month", "Mese"], ["year", "Anno"], ["all", "Totale"], ["custom", "Personalizzato"]];
function regRange(mode, anchor, cs, ce) {
  const y = anchor.getFullYear(), m = anchor.getMonth();
  if (mode === "week") { const s = startOfWeek(anchor); const e = addDays(s, 6); return { start: iso(s), end: iso(e), label: `${fmtD(s)} – ${fmtD(e)} ${e.getFullYear()}` }; }
  if (mode === "month") { const s = new Date(y, m, 1); const e = new Date(y, m + 1, 0); return { start: iso(s), end: iso(e), label: anchor.toLocaleDateString("it-IT", { month: "long", year: "numeric" }) }; }
  if (mode === "year") return { start: `${y}-01-01`, end: `${y}-12-31`, label: String(y) };
  if (mode === "custom") return { start: cs || "2000-01-01", end: ce || "2100-12-31", label: (cs && ce) ? `${cs} – ${ce}` : "Personalizzato" };
  return { start: "2000-01-01", end: "2100-12-31", label: "Tutto lo storico" };
}

function RegistroDialog({ open, onClose, stableId, horses = [], instructors = [] }) {
  const dlgStyle = useMobileDialogStyle();
  const [mode, setMode] = useState("month");
  const [anchor, setAnchor] = useState(new Date());
  const [cs, setCs] = useState(""); const [ce, setCe] = useState("");
  const [q, setQ] = useState("");
  const [instructorId, setInstructorId] = useState("");
  const [reg, setReg] = useState({ students: [], horses: [] });
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null); // {type:'student'|'horse', ...}
  const [tab, setTab] = useState("actual"); // 'actual' (consuntivo) | 'planned' (calendario)
  const [pq, setPq] = useState("");
  const [plannedHorse, setPlannedHorse] = useState(null);
  const range = regRange(mode, anchor, cs, ce);
  const canNav = mode === "week" || mode === "month" || mode === "year";
  const nav = (dir) => { const a = new Date(anchor); if (mode === "week") a.setDate(a.getDate() + dir * 7); else if (mode === "month") a.setMonth(a.getMonth() + dir); else if (mode === "year") a.setFullYear(a.getFullYear() + dir); setAnchor(a); };

  useEffect(() => {
    if (!open || !stableId || detail || tab !== "actual") return;
    setLoading(true);
    api.get(`/stables/${stableId}/calendar/attendance/registry`, { params: { start: range.start, end: range.end, q, instructor_id: instructorId || undefined } })
      .then((r) => setReg(r.data || { students: [], horses: [] })).catch((e) => toast.error(errText(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [open, stableId, mode, anchor, cs, ce, q, instructorId, detail, tab]);  // eslint-disable-line

  useEffect(() => { if (!open) { setDetail(null); setQ(""); setInstructorId(""); setTab("actual"); setPq(""); setPlannedHorse(null); } }, [open]);

  const PeriodBar = (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1" data-testid="reg-mode-selector">
        {REG_MODES.map(([v, l]) => (
          <button key={v} data-testid={`reg-mode-${v}`} onClick={() => setMode(v)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${mode === v ? "bg-[#1B3B2B] text-white border-[#1B3B2B]" : "bg-white text-[#1B3B2B] border-[#E5DEC9]"}`}>{l}</button>
        ))}
      </div>
      {mode === "custom" ? (
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Da</Label><DateField testId="reg-custom-start" className="mt-1" value={cs} onChange={setCs} /></div>
          <div><Label className="text-xs">A</Label><DateField testId="reg-custom-end" className="mt-1" value={ce} onChange={setCe} /></div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2">
          {canNav ? <Button variant="outline" size="icon" data-testid="reg-prev" onClick={() => nav(-1)}><ChevronLeft className="h-4 w-4" /></Button> : <span />}
          <p className="text-sm font-semibold text-[#1B3B2B] capitalize" data-testid="reg-period-label">{range.label}</p>
          {canNav ? <Button variant="outline" size="icon" data-testid="reg-next" onClick={() => nav(1)}><ChevronRight className="h-4 w-4" /></Button> : <span />}
        </div>
      )}
    </div>
  );

  const activeInstr = instructors.filter((i) => i.active !== false);
  const InstrBar = activeInstr.length > 0 && (
    <Select value={instructorId || "all"} onValueChange={(v) => setInstructorId(v === "all" ? "" : v)}>
      <SelectTrigger data-testid="reg-instructor-filter" className="h-9 text-xs"><GraduationCap className="h-3.5 w-3.5 mr-1" /><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Istruttore: Tutti</SelectItem>
        {activeInstr.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const nq = pq.trim().toLowerCase();
  const pmatches = nq ? horses.filter((h) => (h.display_name || "").toLowerCase().includes(nq) || (h.microchip || "").toLowerCase().includes(nq) || (h.ueln || "").toLowerCase().includes(nq)).slice(0, 10) : [];
  const TabBar = (
    <ViewTabs value={tab} onChange={setTab} data-testid="reg-tab-selector" options={[
      { value: "planned", label: "Pianificate", testid: "reg-tab-planned" },
      { value: "actual", label: "Consuntivo", testid: "reg-tab-actual" },
    ]} />
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={dlgStyle} className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="registro-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{detail ? (detail.type === "horse" ? `Utilizzo — ${detail.name}` : `Presenze — ${detail.name}`) : (tab === "planned" ? (plannedHorse ? `Impegni pianificati — ${plannedHorse.name}` : "Registro attività") : "Registro attività")}</DialogTitle>
          <DialogDescription className="sr-only">Registro attività: impegni pianificati e consuntivo presenze per allievo, cavallo e periodo.</DialogDescription>
        </DialogHeader>
        {detail && detail.type === "student" ? (
          <StudentDetail stableId={stableId} range={range} periodBar={PeriodBar} student={detail} instructorId={instructorId} onBack={() => setDetail(null)} />
        ) : detail && detail.type === "horse" ? (
          <HorseDetail stableId={stableId} range={range} periodBar={PeriodBar} horse={detail} instructorId={instructorId} onBack={() => setDetail(null)} />
        ) : tab === "planned" ? (
          plannedHorse ? (
            <PlannedHorseDetail stableId={stableId} range={range} periodBar={<div className="space-y-2">{TabBar}{PeriodBar}</div>} horse={plannedHorse} instructorId={instructorId} onBack={() => setPlannedHorse(null)} />
          ) : (
            <div className="space-y-3">
              {TabBar}
              {PeriodBar}
              {InstrBar}
              <Input data-testid="reg-planned-search" value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Cerca cavallo (nome, microchip, UELN)…" />
              <div className="space-y-1.5" data-testid="reg-planned-horse-list">
                {pmatches.map((h, i) => (
                  <button key={h.id} data-testid={`reg-planned-horse-${i}`} onClick={() => setPlannedHorse({ horse_id: h.id, name: h.display_name })}
                    className="w-full text-left rounded-lg border border-[#E5DEC9] bg-white px-3 py-2 hover:bg-[#F0EAE1] transition-colors flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-[#1B3B2B]">{h.display_name}</span>
                    {(h.microchip || h.ueln) && <span className="text-[10px] text-[#8C6D4F] shrink-0">{h.microchip || h.ueln}</span>}
                  </button>
                ))}
                {pq.trim() && pmatches.length === 0 && <p className="text-sm text-[#5F6B64] text-center py-6">Nessun cavallo trovato.</p>}
                {!pq.trim() && <p className="text-sm text-[#5F6B64] text-center py-6">Cerca un cavallo per vedere i suoi impegni pianificati (dal calendario, indipendentemente dalle presenze).</p>}
              </div>
            </div>
          )
        ) : (
          <div className="space-y-3">
            {TabBar}
            {PeriodBar}
            {InstrBar}
            <Input data-testid="reg-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca allievo o cavallo…" />
            {loading ? <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#8C532B]" /></div> : (
              <div className="space-y-3">
                {reg.students.length === 0 && reg.horses.length === 0 && <p className="text-sm text-[#5F6B64] text-center py-6">Nessuna presenza consuntivata nel periodo.</p>}
                {reg.students.length > 0 && (
                  <div className="space-y-1.5" data-testid="reg-list">
                    <p className="font-mono-label text-[11px] uppercase text-[#8C532B] px-1">Allievi</p>
                    {reg.students.map((it, i) => (
                      <button key={it.student_id || i} data-testid={`reg-row-${i}`} onClick={() => setDetail({ type: "student", student_id: it.student_id, name: it.name })}
                        className="w-full text-left rounded-lg border border-[#E5DEC9] bg-white px-3 py-2 hover:bg-[#F0EAE1] transition-colors">
                        <p className="text-sm font-semibold text-[#1B3B2B]">{it.name}</p>
                        <p className="text-[11px] text-[#5F6B64]">
                          <span className="text-[#2E7D5B] font-semibold">{it.present} presenze</span> · {it.absent} assenze
                          {it.prova > 0 && <> · <span style={{ color: PROVA_INK }}>{it.prova} prove</span></>}
                          {it.unrecorded > 0 && <> · <span className="text-[#8C6D4F]">{it.unrecorded} da registrare</span></>}
                          {it.cancelled > 0 && <> · <span className="text-[#9A8E76]">{it.cancelled} annullata</span></>}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
                {reg.horses.length > 0 && (
                  <div className="space-y-1.5" data-testid="reg-horse-list">
                    <p className="font-mono-label text-[11px] uppercase text-[#8C532B] px-1">Cavalli</p>
                    {reg.horses.map((h, i) => (
                      <button key={h.horse_id || i} data-testid={`reg-horse-row-${i}`} onClick={() => setDetail({ type: "horse", horse_id: h.horse_id, name: h.horse_name })}
                        className="w-full text-left rounded-lg border border-[#E5DEC9] bg-white px-3 py-2 hover:bg-[#F0EAE1] transition-colors">
                        <p className="text-sm font-semibold text-[#1B3B2B]">{h.horse_name}</p>
                        <p className="text-[11px] text-[#5F6B64]"><span className="text-[#2E7D5B] font-semibold">{h.present} utilizzi</span> · {h.students} allievi</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function HorseDetail({ stableId, range, periodBar, horse, instructorId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get(`/stables/${stableId}/calendar/attendance/horse`, { params: { start: range.start, end: range.end, horse_id: horse.horse_id || undefined, name: horse.horse_id ? undefined : horse.name, instructor_id: instructorId || undefined } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errText(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [stableId, range.start, range.end, horse, instructorId]);  // eslint-disable-line
  return (
    <div className="space-y-3">
      <button data-testid="reg-detail-back" onClick={onBack} className="flex items-center gap-1 text-sm text-[#8C532B] hover:underline"><ChevronLeft className="h-4 w-4" />Registro</button>
      {periodBar}
      <div className="rounded-lg border border-[#E5DEC9] bg-white py-2 text-center" data-testid="reg-horse-summary">
        <p className="text-lg font-bold text-[#2E7D5B]">{data?.present ?? 0}</p><p className="text-[9px] text-[#5F6B64]">Utilizzi effettivi (presenze)</p>
      </div>
      {loading ? <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#8C532B]" /></div> : (
        <div className="space-y-2" data-testid="reg-horse-detail-list">
          {(data?.records || []).length === 0 && <p className="text-sm text-[#5F6B64] text-center py-6">Nessun utilizzo consuntivato nel periodo.</p>}
          {groupRecordsByDate(data?.records || []).map((g) => (
            <div key={g.date} data-testid={`reg-horse-day-${g.date}`} className="rounded-lg border border-[#E5DEC9] bg-white overflow-hidden">
              <div className="px-3 py-1.5 bg-[#FAF8F5] border-b border-[#F0EAE1] flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-[#1B3B2B] capitalize">{fmtDayFull(g.date)}</p>
                {g.items.length > 1 && <span className="text-[10px] text-[#8C532B] font-semibold shrink-0">{g.items.length} utilizzi</span>}
              </div>
              <div className="divide-y divide-[#F0EAE1]">
                {g.items.map((r, i) => (
                  <div key={i} data-testid={`reg-horse-detail-row-${g.date}-${i}`} className="px-3 py-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-[#1B3B2B] tabular-nums">{r.start_time}</span>
                      <span className="text-[11px] font-semibold text-[#2E7D5B]">{r.student_name}</span>
                    </div>
                    {r.instructor_name && <p className="text-[11px] text-[#5F6B64]">Istruttore: {r.instructor_name}</p>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PlannedHorseDetail({ stableId, range, periodBar, horse, instructorId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get(`/stables/${stableId}/calendar/planned/horse`, { params: { start: range.start, end: range.end, horse_id: horse.horse_id || undefined, name: horse.horse_id ? undefined : horse.name, instructor_id: instructorId || undefined } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errText(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [stableId, range.start, range.end, horse, instructorId]);  // eslint-disable-line
  const stLabel = { present: ["Presente", "#2E7D5B"], absent: ["Assente", "#B91C1C"], prova: ["Prova", PROVA_INK], unrecorded: ["Non registrato", "#8C6D4F"] };
  const recs = (data?.records || []).filter((r) => !r.is_cancelled);
  return (
    <div className="space-y-3">
      <button data-testid="reg-detail-back" onClick={onBack} className="flex items-center gap-1 text-sm text-[#8C532B] hover:underline"><ChevronLeft className="h-4 w-4" />Registro</button>
      {periodBar}
      <div className="grid grid-cols-2 gap-2" data-testid="reg-planned-summary">
        <div className="rounded-lg border border-[#E5DEC9] bg-white py-2 text-center"><p className="text-lg font-bold text-[#1B3B2B]">{data?.total ?? 0}</p><p className="text-[9px] text-[#5F6B64]">Impegni pianificati</p></div>
        <div className="rounded-lg border border-[#E5DEC9] bg-white py-2 text-center"><p className="text-lg font-bold text-[#2E7D5B]">{data?.upcoming ?? 0}</p><p className="text-[9px] text-[#5F6B64]">Prossimi</p></div>
      </div>
      {loading ? <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#8C532B]" /></div> : (
        <div className="space-y-2" data-testid="reg-planned-detail-list">
          {recs.length === 0 && <p className="text-sm text-[#5F6B64] text-center py-6">Nessun impegno pianificato nel periodo.</p>}
          {groupRecordsByDate(recs).map((g) => (
            <div key={g.date} data-testid={`reg-planned-day-${g.date}`} className="rounded-lg border border-[#E5DEC9] bg-white overflow-hidden">
              <div className="px-3 py-1.5 bg-[#FAF8F5] border-b border-[#F0EAE1]"><p className="text-xs font-semibold text-[#1B3B2B] capitalize">{fmtDayFull(g.date)}</p></div>
              <div className="divide-y divide-[#F0EAE1]">
                {g.items.map((r, i) => {
                  const [lbl, col] = stLabel[r.att_status] || stLabel.unrecorded;
                  const changed = r.att_status === "present" && r.actual_horse_name && r.planned_horse_name && r.actual_horse_name !== r.planned_horse_name;
                  return (
                    <div key={i} data-testid={`reg-planned-row-${g.date}-${i}`} className="px-3 py-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-[#1B3B2B] tabular-nums">{r.start_time}–{r.end_time}</span>
                        <span className="text-[10px] font-semibold" style={{ color: col }}>{lbl}</span>
                      </div>
                      <p className="text-[11px] text-[#1B3B2B]">{r.title}{r.student_name ? ` · ${r.student_name}` : ""}</p>
                      {r.instructor_name && <p className="text-[11px] text-[#5F6B64]">Istruttore: {r.instructor_name}</p>}
                      {changed && <p className="text-[10px] text-[#8C6D4F]">Usato realmente: {r.actual_horse_name}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StudentDetail({ stableId, range, periodBar, student, instructorId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get(`/stables/${stableId}/calendar/attendance/student`, { params: { start: range.start, end: range.end, student_id: student.student_id || undefined, name: student.student_id ? undefined : student.name, instructor_id: instructorId || undefined } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errText(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [stableId, range.start, range.end, student, instructorId]);  // eslint-disable-line
  const s = data?.summary || { present: 0, absent: 0, prova: 0, unrecorded: 0, cancelled: 0 };
  const stColor = { present: "#2E7D5B", absent: "#B91C1C", unrecorded: "#5F6B64" };
  return (
    <div className="space-y-3">
      <button data-testid="reg-detail-back" onClick={onBack} className="flex items-center gap-1 text-sm text-[#8C532B] hover:underline"><ChevronLeft className="h-4 w-4" />Registro</button>
      {periodBar}
      <div className="grid grid-cols-5 gap-1.5 text-center" data-testid="reg-detail-summary">
        {[["Presenze", s.present, "#2E7D5B"], ["Assenze", s.absent, "#B91C1C"], ["Prove", s.prova, PROVA_INK], ["Da reg.", s.unrecorded, "#8C6D4F"], ["Annullate", s.cancelled, "#9A8E76"]].map(([l, v, c]) => (
          <div key={l} className="rounded-lg border border-[#E5DEC9] bg-white py-2">
            <p className="text-lg font-bold" style={{ color: c }}>{v}</p><p className="text-[9px] text-[#5F6B64]">{l}</p>
          </div>
        ))}
      </div>
      {loading ? <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#8C532B]" /></div> : (
        <div className="space-y-2" data-testid="reg-detail-list">
          {(data?.records || []).length === 0 && <p className="text-sm text-[#5F6B64] text-center py-6">Nessuna partecipazione nel periodo.</p>}
          {groupRecordsByDate(data?.records || []).map((g) => (
            <div key={g.date} data-testid={`reg-detail-day-${g.date}`} className="rounded-lg border border-[#E5DEC9] bg-white overflow-hidden">
              <div className="px-3 py-1.5 bg-[#FAF8F5] border-b border-[#F0EAE1] flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-[#1B3B2B] capitalize">{fmtDayFull(g.date)}</p>
                {g.items.length > 1 && <span className="text-[10px] text-[#8C532B] font-semibold shrink-0">{g.items.length} lezioni</span>}
              </div>
              <div className="divide-y divide-[#F0EAE1]">
                {g.items.map((r, i) => {
                  const diff = r.planned_horse_name && r.planned_horse_name !== r.actual_horse_name;
                  return (
                    <div key={i} data-testid={`reg-detail-row-${g.date}-${i}`} className={`px-3 py-1.5 ${r.cancelled ? "bg-[#F7F4EE]" : ""}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-[#1B3B2B] tabular-nums">{r.start_time}</span>
                        {r.cancelled
                          ? <span className="text-[11px] font-semibold text-[#8C7A5E]" data-testid={`reg-detail-cancelled-${g.date}-${i}`}>Annullata</span>
                          : <span className="text-[11px] font-semibold" style={{ color: stColor[r.status] || "#5F6B64" }}>{ST_LABEL[r.status] || "—"}{r.kind === "prova" && <span className="ml-1" style={{ color: PROVA_INK }}>· PROVA</span>}</span>}
                      </div>
                      {!r.cancelled && <p className="text-[11px] text-[#5F6B64]">
                        {r.status === "absent" ? "—" : (r.actual_horse_name || "Nessun cavallo")}{r.instructor_name ? ` · ${r.instructor_name}` : ""}
                      </p>}
                      {!r.cancelled && diff && r.status !== "absent" && <p className="text-[11px] text-[#8C6D4F]" data-testid={`reg-detail-planned-${g.date}-${i}`}>Previsto: {r.planned_horse_name}</p>}
                      {r.note && <p className="text-[11px] text-[#8C532B] italic mt-0.5">{r.note}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function FindSlotsDialog({ open, onClose, stableId, horses, instructors = [], onPick }) {
  const dlgStyle = useMobileDialogStyle();
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");
  const [instructorId, setInstructorId] = useState("");
  const [weekAnchor, setWeekAnchor] = useState(startOfWeek(new Date()));
  const [fsDay, setFsDay] = useState(startOfWeek(new Date()));
  const [result, setResult] = useState(null);
  const [noInstr, setNoInstr] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) { setSelected([]); setQ(""); setInstructorId(""); setResult(null); setNoInstr(false); setWeekAnchor(startOfWeek(new Date())); setFsDay(startOfWeek(new Date())); } }, [open]);
  const usable = horses.filter((h) => h.usable_in_lessons !== false);
  const matches = q.trim() ? usable.filter((h) => matchHorse({ horse_name: h.display_name, horse_official_name: h.official_name, horse_microchip: h.microchip, horse_ueln: h.ueln }, q)) : [];
  const toggle = (h) => setSelected((s) => s.find((x) => x.id === h.id) ? s.filter((x) => x.id !== h.id) : [...s, h]);
  const search = async (anchor) => {
    setLoading(true);
    try {
      const from = iso(anchor); const to = iso(addDays(anchor, 41));
      const params = { horse_ids: selected.map((h) => h.id).join(","), date_from: from, date_to: to };
      if (instructorId) params.instructor_id = instructorId;
      const r = await api.get(`/stables/${stableId}/calendar/find-slots`, { params });
      setNoInstr(!!r.data.no_instructors);
      const map = {}; (r.data.slots || []).forEach((s) => { map[`${s.date}|${s.start}`] = s; });
      setResult(map);
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  const weekDays = [...Array(7)].map((_, i) => addDays(weekAnchor, i));
  const rowStarts = result ? [...new Set(Object.values(result).map((s) => s.start))].sort() : [];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={dlgStyle} className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="find-slots-dialog">
        <DialogHeader><DialogTitle className="font-display">Trova disponibilità</DialogTitle><DialogDescription className="sr-only">Seleziona i cavalli e visualizza gli slot in cui sono tutti liberi.</DialogDescription></DialogHeader>
        <p className="text-xs text-[#5F6B64]">Seleziona i cavalli desiderati: Equiro evidenzia gli <b>slot operativi</b> in cui sono tutti liberi.</p>
        <div>
          <div className="flex flex-wrap gap-1.5 my-1.5">{selected.map((h) => <span key={h.id} data-testid={`fs-sel-${h.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#1B3B2B] text-white text-xs px-2.5 py-1">{h.display_name}<button onMouseDown={(e) => { e.preventDefault(); toggle(h); }}><X className="h-3 w-3" /></button></span>)}</div>
          <Input data-testid="fs-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cavallo…" />
          {matches.length > 0 && <div className="mt-1 max-h-40 overflow-auto rounded-lg border border-[#E5DEC9]">{matches.slice(0, 20).map((h) => <button key={h.id} type="button" data-testid={`fs-opt-${h.id}`} onClick={() => { toggle(h); setQ(""); }} className="w-full text-left px-3 py-2 text-sm hover:bg-[#F0EAE1] text-[#1B3B2B]">{h.display_name}</button>)}</div>}
        </div>
        {instructors.length > 0 && (
          <div><Label className="text-xs">Istruttore</Label>
            <Select value={instructorId || "any"} onValueChange={(v) => setInstructorId(v === "any" ? "" : v)}>
              <SelectTrigger data-testid="fs-instructor" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="any">Qualsiasi</SelectItem>{instructors.filter((i) => i.active).map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <Button onClick={() => search(weekAnchor)} disabled={loading} data-testid="fs-run" className="bg-[#1B3B2B] hover:bg-[#142E1F]">{loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Trova slot disponibili</Button>
        {result !== null && noInstr && (
          <div data-testid="fs-no-instructors" className="rounded-lg border border-[#E5C98A] bg-[#FBF6E8] px-4 py-6 text-center">
            <p className="text-sm font-semibold text-[#8C6A1F]">Nessuna disponibilità istruttori configurata</p>
            <p className="text-xs text-[#8C6A1F] mt-1">La ricerca disponibilità usa esclusivamente le disponibilità degli istruttori. Configurale in Impostazioni calendario → Istruttori.</p>
          </div>
        )}
        {result !== null && !noInstr && (
          <div data-testid="fs-results">
            <div className="flex items-center justify-between mb-2">
              <Button variant="outline" size="icon" data-testid="fs-week-prev" onClick={() => { const a = addDays(weekAnchor, -7); setWeekAnchor(a); setFsDay(a); }}><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-sm font-semibold text-[#1B3B2B]">{fmtD(weekDays[0])} – {fmtD(weekDays[6])}</span>
              <Button variant="outline" size="icon" data-testid="fs-week-next" onClick={() => { const a = addDays(weekAnchor, 7); setWeekAnchor(a); setFsDay(a); }}><ChevronRight className="h-4 w-4" /></Button>
            </div>
            {rowStarts.length === 0 ? <p className="text-sm text-[#5F6B64] text-center py-4">Nessuno slot disponibile nel periodo (istruttore o cavallo occupati).</p> : (<>
              <div className="overflow-x-auto" data-testid="fs-mobile"><div className="grid gap-px bg-[#EFE8D6] rounded-lg overflow-hidden" style={{ gridTemplateColumns: `32px repeat(7, minmax(0,1fr))` }}>
                <div className="bg-[#FAF8F5]" />
                {weekDays.map((d) => <div key={iso(d)} className="bg-[#FAF8F5] text-center py-1"><p className="text-[9px] font-mono-label text-[#8C532B]">{WD[wdOf(d)]}</p><p className="text-[11px] font-semibold text-[#1B3B2B]">{d.getDate()}</p></div>)}
                {rowStarts.map((start) => (<FSRow key={start} start={start} weekDays={weekDays} result={result} onPick={onPick} selected={selected} />))}
              </div></div>
            </>)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
function FSRow({ start, weekDays, result, onPick, selected }) {
  return (
    <>
      <div className="bg-[#FAF8F5] text-right px-1 py-2 text-[11px] font-semibold text-[#1B3B2B] tabular-nums">{start}</div>
      {weekDays.map((d) => {
        const slot = result[`${iso(d)}|${start}`];
        if (slot) return <button key={iso(d)} data-testid={`fs-slot-${iso(d)}-${start}`} onClick={() => onPick(slot, selected.map((h) => h.id))} className="bg-[#DFF0E0] hover:bg-[#C6E6C8] min-h-[36px] transition-colors" title="Disponibile" />;
        return <div key={iso(d)} className="bg-white min-h-[36px]" />;
      })}
    </>
  );
}

function SlotsDialog({ open, onClose, settings, setSettings, stableId, onSaved, onInstructorsChanged, onArenasChanged }) {
  const dlgStyle = useMobileDialogStyle();
  const [slots, setSlots] = useState([]);
  const [holidays, setHolidays] = useState(false);
  const [closingDays, setClosingDays] = useState([]);
  const [weekendDays, setWeekendDays] = useState([5, 6]);
  const [specials, setSpecials] = useState([]);
  const [nd, setNd] = useState({ date: "", label: "", repeat_yearly: false });
  const [saving, setSaving] = useState(false);
  const [instrs, setInstrs] = useState([]);
  const [inf, setInf] = useState(null);
  const loadInstrs = () => api.get(`/stables/${stableId}/instructors`).then((r) => setInstrs(r.data || [])).catch(() => {});
  const [arenaList, setArenaList] = useState([]);
  const [newArena, setNewArena] = useState("");
  const loadArenas = () => api.get(`/stables/${stableId}/arenas`).then((r) => setArenaList(r.data || [])).catch(() => {});
  const addArena = async () => { if (!newArena.trim()) return; try { await api.post(`/stables/${stableId}/arenas`, { name: newArena.trim() }); setNewArena(""); await loadArenas(); onArenasChanged && onArenasChanged(); } catch (e) { toast.error(errText(e.response?.data?.detail)); } };
  const delArena = async (id) => { try { await api.delete(`/stables/${stableId}/arenas/${id}`); await loadArenas(); onArenasChanged && onArenasChanged(); } catch (e) { toast.error(errText(e.response?.data?.detail)); } };
  useEffect(() => {
    if (open) {
      setSlots(JSON.parse(JSON.stringify(settings.slots || [])));
      setHolidays(!!settings.holidays_enabled);
      setClosingDays(settings.closing_days || []);
      setWeekendDays(settings.weekend_days ?? [5, 6]);
      api.get(`/stables/${stableId}/calendar/special-days`, { params: { start: `${new Date().getFullYear()}-01-01`, end: `${new Date().getFullYear() + 1}-12-31` } })
        .then((r) => setSpecials((r.data || []).flatMap((x) => x.items.filter((it) => it.kind === "custom").map((it) => ({ ...it, date: x.date }))))).catch(() => {});
      loadInstrs(); setInf(null); loadArenas(); setNewArena("");
    }
  }, [open, settings, stableId]);
  const saveInstr = async () => {
    if (!inf.first_name.trim()) { toast.error("Nome obbligatorio"); return; }
    try {
      if (inf.id) await api.put(`/stables/${stableId}/instructors/${inf.id}`, inf);
      else await api.post(`/stables/${stableId}/instructors`, inf);
      setInf(null); await loadInstrs(); onInstructorsChanged && onInstructorsChanged(); toast.success("Istruttore salvato");
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const delInstr = async (id) => { try { await api.delete(`/stables/${stableId}/instructors/${id}`); await loadInstrs(); onInstructorsChanged && onInstructorsChanged(); } catch (e) { toast.error(errText(e.response?.data?.detail)); } };
  const blankInstr = { first_name: "", last_name: "", active: true, user_id: null, color: null, availability: [] };
  const setAv = (idx, patch) => setInf((f) => ({ ...f, availability: f.availability.map((a, j) => j === idx ? { ...a, ...patch } : a) }));
  const toggleAvWd = (idx, n) => setAv(idx, { weekdays: inf.availability[idx].weekdays.includes(n) ? inf.availability[idx].weekdays.filter((x) => x !== n) : [...inf.availability[idx].weekdays, n] });
  const add = () => setSlots([...slots, { weekdays: [1, 2, 3, 4], start: "17:00", end: "18:00", color: COLORS[7], label: "" }]);
  const upd = (i, patch) => setSlots(slots.map((s, j) => j === i ? { ...s, ...patch } : s));
  const toggleWd = (i, n) => upd(i, { weekdays: slots[i].weekdays.includes(n) ? slots[i].weekdays.filter((x) => x !== n) : [...slots[i].weekdays, n] });
  const toggleDay = (arr, set, n) => set(arr.includes(n) ? arr.filter((x) => x !== n) : [...arr, n]);
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/stables/${stableId}/calendar/settings`, { slots, holidays_enabled: holidays, closing_days: closingDays, weekend_days: weekendDays });
      setSettings({ slots: r.data.slots, holidays_enabled: r.data.holidays_enabled, closing_days: r.data.closing_days, weekend_days: r.data.weekend_days });
      toast.success("Impostazioni salvate"); onSaved && onSaved(); onClose();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const addSpecial = async () => {
    if (!nd.date || !nd.label.trim()) { toast.error("Inserisci data ed etichetta"); return; }
    try { const r = await api.post(`/stables/${stableId}/calendar/special-days`, nd); setSpecials([...specials, { ...nd, id: r.data.id, kind: "custom" }]); setNd({ date: "", label: "", repeat_yearly: false }); toast.success("Giornata speciale aggiunta"); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const delSpecial = async (id) => { try { await api.delete(`/stables/${stableId}/calendar/special-days/${id}`); setSpecials(specials.filter((s) => s.id !== id)); } catch (e) { toast.error(errText(e.response?.data?.detail)); } };
  const DayToggle = ({ arr, set, testid }) => (
    <div className="flex gap-1" data-testid={testid}>{WD.map((w, n) => <button key={n} type="button" onClick={() => toggleDay(arr, set, n)} className={`h-7 w-7 rounded-full text-[11px] font-semibold ${arr.includes(n) ? "bg-[#8C532B] text-white" : "bg-white border border-[#E5DEC9] text-[#1B3B2B]"}`}>{w[0]}</button>)}</div>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={dlgStyle} className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="slots-dialog">
        <DialogHeader><DialogTitle className="font-display">Slot & festività</DialogTitle><DialogDescription className="sr-only">Griglia oraria abituale, giorni di chiusura, weekend e giornate speciali.</DialogDescription></DialogHeader>
        <div>
          <p className="text-sm font-semibold text-[#1B3B2B]">Slot operativi standard</p>
          <p className="text-xs text-[#5F6B64] mb-2">Griglia oraria abituale della scuderia. NON crea lezioni: alimenta la ricerca disponibilità e la creazione rapida.</p>
          <div className="space-y-2">
            {slots.map((s, i) => (
              <div key={i} data-testid={`slot-${i}`} className="rounded-lg border border-[#E5DEC9] p-2 space-y-2">
                <div className="flex gap-1">{WD.map((w, n) => <button key={n} onClick={() => toggleWd(i, n)} className={`h-7 w-7 rounded-full text-[11px] font-semibold ${s.weekdays.includes(n) ? "bg-[#8C532B] text-white" : "bg-white border border-[#E5DEC9]"}`}>{w[0]}</button>)}</div>
                <div className="flex gap-2 items-center">
                  <Input type="time" className="w-28" value={s.start} onChange={(e) => upd(i, { start: e.target.value })} />
                  <Input type="time" className="w-28" value={s.end} onChange={(e) => upd(i, { end: e.target.value })} />
                  <button onClick={() => setSlots(slots.filter((_, j) => j !== i))} className="text-red-500 ml-auto"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" data-testid="add-slot" onClick={add}><Plus className="h-3.5 w-3.5 mr-1" />Aggiungi slot</Button>
          </div>
        </div>
        <div className="border-t border-[#E5DEC9] pt-3 space-y-2">
          <div><p className="text-sm font-semibold text-[#1B3B2B]">Giorni di chiusura abituale</p><p className="text-xs text-[#5F6B64] mb-1.5">Attenuati sul calendario ed esclusi da «Trova disponibilità». È comunque possibile creare una lezione.</p><DayToggle arr={closingDays} set={setClosingDays} testid="closing-days" /></div>
          <div><p className="text-sm font-semibold text-[#1B3B2B]">Giorni weekend / non ordinari</p><p className="text-xs text-[#5F6B64] mb-1.5">Solo evidenziazione leggera. Distinto da chiusura e festività.</p><DayToggle arr={weekendDays} set={setWeekendDays} testid="weekend-days" /></div>
          <label className="flex items-center justify-between gap-3 cursor-pointer pt-1"><span><span className="text-sm font-semibold text-[#1B3B2B]">Festività nazionali</span><span className="block text-xs text-[#5F6B64]">Evidenzia le principali festività italiane.</span></span><Switch data-testid="holidays-toggle" checked={holidays} onCheckedChange={setHolidays} /></label>
        </div>
        <div className="border-t border-[#E5DEC9] pt-3">
          <p className="text-sm font-semibold text-[#1B3B2B] mb-1">Giornate speciali</p>
          <p className="text-xs text-[#5F6B64] mb-2">Patrono, chiusure, ferie… «Ripeti ogni anno» per le ricorrenze annuali.</p>
          <div className="space-y-1.5 mb-2">{specials.map((s) => <div key={s.id} data-testid={`special-row-${s.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-[#E5DEC9] px-3 py-1.5"><span className="text-sm text-[#1B3B2B] truncate">{s.label} <span className="text-xs text-[#8C6D4F]">· {s.date}{s.repeat_yearly ? " (ogni anno)" : ""}</span></span><button onClick={() => delSpecial(s.id)} className="text-red-500 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button></div>)}</div>
          <div className="rounded-lg border border-[#E5DEC9] p-2 space-y-2">
            <div className="grid grid-cols-2 gap-2"><div><Label className="text-xs">Data</Label><DateField testId="special-date" className="mt-1" value={nd.date} onChange={(v) => setNd({ ...nd, date: v })} /></div><div><Label className="text-xs">Etichetta</Label><Input data-testid="special-label" className="mt-1" value={nd.label} onChange={(e) => setNd({ ...nd, label: e.target.value })} placeholder="Es. Patrono" /></div></div>
            <label className="flex items-center gap-2 text-sm text-[#5F6B64]"><Switch data-testid="special-yearly" checked={nd.repeat_yearly} onCheckedChange={(v) => setNd({ ...nd, repeat_yearly: v })} />Ripeti ogni anno</label>
            <Button variant="outline" size="sm" data-testid="add-special" onClick={addSpecial}><Plus className="h-3.5 w-3.5 mr-1" />Aggiungi giornata speciale</Button>
          </div>
        </div>
        <div className="border-t border-[#E5DEC9] pt-3" data-testid="instructors-section">
          <p className="text-sm font-semibold text-[#1B3B2B] mb-1 flex items-center gap-1.5"><GraduationCap className="h-4 w-4" />Istruttori / Tecnici</p>
          <p className="text-xs text-[#5F6B64] mb-2">Chi svolge le lezioni e la sua disponibilità settimanale (distinta dagli slot della scuderia).</p>
          <div className="space-y-1.5 mb-2">{instrs.map((it) => (
            <div key={it.id} data-testid={`instructor-row-${it.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-[#E5DEC9] px-3 py-1.5">
              <span className="text-sm text-[#1B3B2B] truncate">{it.name}{!it.active && <span className="text-xs text-[#9A8E76]"> · non attivo</span>}<span className="text-xs text-[#8C6D4F]"> · {(it.availability || []).length} fasce</span></span>
              <span className="flex gap-2 shrink-0"><button data-testid={`instructor-edit-${it.id}`} onClick={() => setInf({ ...blankInstr, ...it, availability: it.availability || [] })} className="text-[#8C532B] text-xs hover:underline">Modifica</button><button onClick={() => delInstr(it.id)} className="text-red-500"><Trash2 className="h-3.5 w-3.5" /></button></span>
            </div>
          ))}</div>
          {!inf ? (
            <Button variant="outline" size="sm" data-testid="add-instructor" onClick={() => setInf({ ...blankInstr })}><Plus className="h-3.5 w-3.5 mr-1" />Aggiungi istruttore</Button>
          ) : (
            <div className="rounded-lg border border-[#E5DEC9] p-2 space-y-2" data-testid="instructor-form">
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Nome</Label><Input data-testid="instr-first" className="mt-1" value={inf.first_name} onChange={(e) => setInf({ ...inf, first_name: e.target.value })} /></div>
                <div><Label className="text-xs">Cognome</Label><Input data-testid="instr-last" className="mt-1" value={inf.last_name} onChange={(e) => setInf({ ...inf, last_name: e.target.value })} /></div>
              </div>
              <label className="flex items-center gap-2 text-sm text-[#5F6B64]"><Switch data-testid="instr-active" checked={inf.active} onCheckedChange={(v) => setInf({ ...inf, active: v })} />Attivo</label>
              <div><Label className="text-xs">Colore identificativo</Label><div className="flex flex-wrap gap-2 mt-1" data-testid="instr-color">{["#2E7D5B", "#2563A8", "#B0006A", "#C4611A", "#6D28D9", "#0E7490", "#B45309", "#9D174D"].map((c) => <button key={c} type="button" onClick={() => setInf({ ...inf, color: c })} style={{ backgroundColor: c }} className={`h-7 w-7 rounded-full transition ${inf.color === c ? "ring-2 ring-offset-2 ring-[#1B3B2B]" : ""}`} />)}</div></div>
              <div className="space-y-1.5">
                <Label className="text-xs">Disponibilità settimanale</Label>
                {inf.availability.map((a, idx) => (
                  <div key={idx} className="rounded border border-[#E5DEC9] p-1.5 space-y-1.5">
                    <div className="flex gap-1">{WD.map((w, n) => <button key={n} type="button" onClick={() => toggleAvWd(idx, n)} className={`h-7 w-7 rounded-full text-[11px] font-semibold ${a.weekdays.includes(n) ? "bg-[#8C532B] text-white" : "bg-white border border-[#E5DEC9]"}`}>{w[0]}</button>)}</div>
                    <div className="flex gap-2 items-center"><Input type="time" className="w-28" value={a.start} onChange={(e) => setAv(idx, { start: e.target.value })} /><Input type="time" className="w-28" value={a.end} onChange={(e) => setAv(idx, { end: e.target.value })} /><button onClick={() => setInf({ ...inf, availability: inf.availability.filter((_, j) => j !== idx) })} className="text-red-500 ml-auto"><Trash2 className="h-4 w-4" /></button></div>
                  </div>
                ))}
                <Button variant="outline" size="sm" data-testid="add-instr-avail" onClick={() => setInf({ ...inf, availability: [...inf.availability, { weekdays: [1, 2, 3, 4], start: "17:00", end: "20:00" }] })}><Plus className="h-3.5 w-3.5 mr-1" />Aggiungi fascia</Button>
              </div>
              <div className="flex gap-2"><Button size="sm" variant="ghost" onClick={() => setInf(null)}>Annulla</Button><Button size="sm" data-testid="save-instructor" onClick={saveInstr} className="bg-[#1B3B2B] hover:bg-[#142E1F]">Salva istruttore</Button></div>
            </div>
          )}
        </div>

        <div className="border-t border-[#E5DEC9] pt-3" data-testid="arenas-section">
          <p className="text-sm font-semibold text-[#1B3B2B] mb-1">Campi / Aree di lavoro</p>
          <p className="text-xs text-[#5F6B64] mb-2">Coperto, campo esterno, tondino… Dato operativo (non mostrato nelle card del planning).</p>
          <div className="space-y-1.5 mb-2">{arenaList.map((a) => (
            <div key={a.id} data-testid={`arena-row-${a.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-[#E5DEC9] px-3 py-1.5">
              <span className="text-sm text-[#1B3B2B] truncate">{a.name}</span>
              <button onClick={() => delArena(a.id)} className="text-red-500 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}</div>
          <div className="flex gap-2"><Input data-testid="arena-name" value={newArena} onChange={(e) => setNewArena(e.target.value)} placeholder="Nome campo" /><Button variant="outline" size="sm" data-testid="add-arena" onClick={addArena}><Plus className="h-3.5 w-3.5 mr-1" />Aggiungi</Button></div>
        </div>

        <DialogFooter><Button variant="outline" onClick={onClose}>Chiudi</Button><Button onClick={save} disabled={saving} data-testid="save-slots" className="bg-[#1B3B2B] hover:bg-[#142E1F]">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salva impostazioni</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
